// Guía de Respuesta a Emergencias 2024 (ERG 2024) — Datos oficiales en español
// Fuente: Transport Canada / PHMSA / SCT — Emergency Response Guidebook 2024

export interface ErgGuide {
  number: number;
  name: string;
  hazards: {
    fireExplosion: string;
    health: string;
  };
  publicSafety: {
    callNumber: string;
    isolationSmallSpill: string;
    isolationLargeSpill: string;
    fireIsolation: string;
    protectiveActionSmall: string;
    protectiveActionLarge: string;
  };
  emergencyResponse: {
    fire: string[];
    spillLeak: string[];
  };
  firstAid: string[];
  fireByType: {
    small: string;
    large: string;
    tank: string;
  };
  ppe: {
    suit: string;
    respirator: string;
    gloves: string;
    boots: string;
  };
}

// ── Catálogo de guías ERG 2024 ────────────────────────────────────────────────

export const ERG_GUIDES: Record<number, ErgGuide> = {

  115: {
    number: 115,
    name: 'Gases Inflamables (Incluye Gases Refrigerantes)',
    hazards: {
      fireExplosion: 'EXTREMADAMENTE INFLAMABLE. Se enciende fácilmente. Puede formar mezclas explosivas con el aire. El vapor es más pesado que el aire y puede desplazarse a distancias considerables hasta una fuente de ignición y regresar el fuego. Los cilindros pueden explotar violentamente cuando se calientan.',
      health: 'La inhalación de vapores puede causar mareo, náuseas, cefalea o inconsciencia. Los gases licuados pueden causar quemaduras criogénicas. La alta concentración puede desplazar el oxígeno y causar asfixia.',
    },
    publicSafety: {
      callNumber: '911 / ECU-911',
      isolationSmallSpill: 'Aislar al menos 100 m en todas las direcciones.',
      isolationLargeSpill: 'Aislar al menos 300 m en todas las direcciones.',
      fireIsolation: 'En incendio de tanques/carros/camiones aislar 800 m. Considerar evacuación inicial 800 m en todas las direcciones.',
      protectiveActionSmall: 'Evacuar 100 m alrededor del derrame.',
      protectiveActionLarge: 'Evacuar 500 m a favor del viento si hay fuga grande.',
    },
    emergencyResponse: {
      fire: [
        'NO apagar el fuego si la fuga no se puede detener.',
        'Usar agua en neblina para enfriar los contenedores expuestos al fuego.',
        'Retirar todos los contenedores del área de incendio si puede hacerse sin riesgo.',
        'Si el fuego es inevitable o si los contenedores se han expuesto al fuego directo RETIRARSE. Dejar que el fuego se queme.',
        'Si el fuego involucra cargas (cisternas), aislar 800 m; también considerar evacuación inicial.',
      ],
      spillLeak: [
        'ELIMINAR todas las fuentes de ignición (no fumar, no usar bengalas, no generar chispas ni llamas en el área de peligro).',
        'Toda la maquinaria debe ser a prueba de explosión.',
        'No tocar ni caminar sobre material derramado.',
        'Detener la fuga si puede hacerse sin riesgo.',
        'Prevenir la entrada en sumideros, sótanos u espacios confinados.',
        'Usar agua en neblina para reducir vapores o desviar las nubes de vapor.',
        'Ventilar las áreas cerradas.',
      ],
    },
    firstAid: [
      'Trasladar a la víctima a un área de aire fresco y mantener el descanso en una posición cómoda para la respiración.',
      'Si la víctima no respira, administrar respiración artificial.',
      'Si la respiración es difícil, administrar oxígeno.',
      'Quitar y aislar la ropa contaminada.',
      'Contacto con piel: lavar con agua corriente y jabón durante 20 minutos.',
      'Contacto con ojos: irrigar con agua a temperatura ambiente durante al menos 20 minutos.',
      'Mantener a la víctima caliente y en reposo.',
      'Trasladar al centro médico inmediatamente.',
    ],
    fireByType: {
      small: 'CO2 o polvo químico seco.',
      large: 'Agua en neblina. NO apagar el fuego si la fuga no puede detenerse.',
      tank: 'AISLAR 800 m. Dejar quemar. Enfriar tanques con agua desde distancia segura.',
    },
    ppe: {
      suit: 'Ropa de bombero estructural resistente al fuego (Nomex)',
      respirator: 'SCBA (equipo autónomo de respiración) presión positiva',
      gloves: 'Guantes resistentes al calor y a los hidrocarburos',
      boots: 'Botas de seguridad antiestáticas resistentes al calor',
    },
  },

  117: {
    number: 117,
    name: 'Gases Tóxicos e Inflamables (Peligro Extremo)',
    hazards: {
      fireExplosion: 'EXTREMADAMENTE INFLAMABLE. Puede formar mezclas explosivas con el aire. El vapor puede desplazarse a considerable distancia hasta una fuente de ignición y regresar el fuego. Los cilindros pueden explotar violentamente cuando se calientan.',
      health: 'MUY TÓXICO; puede ser fatal si se inhala, ingiere o absorbe por la piel. El contacto puede causar quemaduras en la piel y los ojos. El humo del incendio puede ser irritante, tóxico y/o corrosivo. Las concentraciones de vapor en el aire superiores al límite inferior de explosividad son peligrosas.',
    },
    publicSafety: {
      callNumber: '911 / ECU-911',
      isolationSmallSpill: 'Aislar al menos 300 m en todas las direcciones.',
      isolationLargeSpill: 'Aislar al menos 700 m en todas las direcciones.',
      fireIsolation: 'En incendio de tanques/carros aislar 1600 m. Considerar evacuación inicial en todas las direcciones.',
      protectiveActionSmall: 'Evacuar 400 m a favor del viento.',
      protectiveActionLarge: 'Evacuar 1000 m o más a favor del viento.',
    },
    emergencyResponse: {
      fire: [
        'Usar equipo autónomo de respiración (SCBA) presión positiva y ropa de protección química adecuada.',
        'Pequeño incendio: Polvo químico seco o CO2.',
        'Gran incendio: Agua en neblina o espuma resistente al alcohol.',
        'Retirar los contenedores del área de incendio si puede hacerse sin riesgo.',
        'Si el fuego involucra cisternas AISLAR 1600 m; considerar evacuación inmediata.',
      ],
      spillLeak: [
        'ELIMINAR todas las fuentes de ignición.',
        'Usar equipo de protección personal adecuado. Evacuar el área.',
        'No tocar ni caminar sobre el material derramado.',
        'Detener la fuga si puede hacerse sin riesgo.',
        'Si es posible, girar los contenedores que estén fugando para que la fuga sea de gas y no de líquido.',
        'Prevenir la entrada en sumideros, sótanos u espacios confinados.',
        'Usar agua en neblina para reducir vapores.',
      ],
    },
    firstAid: [
      'Trasladar a la víctima fuera del área contaminada con equipo de protección respiratoria.',
      'Llamar inmediatamente al 911 (ambulancia) e indicar el producto y vía de exposición.',
      'Si no respira, administrar respiración artificial con precaución (el vapor puede ser tóxico).',
      'Administrar oxígeno si la respiración es difícil.',
      'Quitar inmediatamente la ropa contaminada y lavar la piel con agua y jabón durante 20 minutos.',
      'Contacto con ojos: irrigar con agua a temperatura ambiente durante al menos 20 minutos.',
      'No inducir el vómito si hubo ingestión.',
      'Trasladar URGENTEMENTE al centro médico.',
    ],
    fireByType: {
      small: 'CO2 o polvo químico seco.',
      large: 'Agua en neblina. NO apagar si la fuga no puede detenerse.',
      tank: 'AISLAR 1600 m. Dejar quemar. Enfriar desde distancia segura con agua.',
    },
    ppe: {
      suit: 'Traje de protección química nivel A (encapsulado) o nivel B',
      respirator: 'SCBA (equipo autónomo de respiración) presión positiva',
      gloves: 'Guantes de protección química resistentes a gases tóxicos',
      boots: 'Botas de seguridad antiestáticas resistentes a químicos',
    },
  },

  121: {
    number: 121,
    name: 'Gases Inertes (No Inflamables)',
    hazards: {
      fireExplosion: 'No inflamable. Los contenedores pueden explotar cuando se calientan. El vapor de ciertos gases (nitrógeno, argón, helio) puede desplazar el oxígeno causando asfixia.',
      health: 'Los vapores pueden causar mareo o asfixia sin advertencia previa de olor. El contacto con el gas o el líquido licuado puede causar quemaduras criogénicas.',
    },
    publicSafety: {
      callNumber: '911 / ECU-911',
      isolationSmallSpill: 'Aislar al menos 25 m en todas las direcciones.',
      isolationLargeSpill: 'Aislar al menos 50 m en todas las direcciones.',
      fireIsolation: 'En incendio de tanques/carros aislar 300 m.',
      protectiveActionSmall: 'Ventilar el área.',
      protectiveActionLarge: 'Evacuar si hay acumulación en espacio cerrado.',
    },
    emergencyResponse: {
      fire: [
        'No presentan riesgo de incendio por sí mismos.',
        'Usar agua en abundancia para enfriar los contenedores expuestos al fuego.',
        'Retirar los contenedores del área de incendio si puede hacerse sin riesgo.',
        'Enfriar con agua los cilindros con gas comprimido hasta que el fuego se extinga.',
      ],
      spillLeak: [
        'No tocar ni caminar sobre material derramado (líquido criogénico).',
        'Detener la fuga si puede hacerse sin riesgo.',
        'Si es posible, girar los contenedores para que la fuga sea de gas y no de líquido.',
        'Usar agua en neblina para dispersar los vapores y proteger al personal.',
        'Ventilar el área cerrada afectada.',
        'Prevenir la entrada de gases en sótanos o espacios confinados.',
      ],
    },
    firstAid: [
      'Trasladar a la víctima a un área de aire fresco.',
      'Si no respira, administrar respiración artificial.',
      'Si la respiración es difícil, administrar oxígeno.',
      'Mantener a la víctima caliente y en reposo.',
      'Quemaduras criogénicas: lavar la zona afectada con agua tibia (NO caliente) durante 20 minutos.',
      'Trasladar al centro médico.',
    ],
    fireByType: {
      small: 'Agua en neblina para enfriar contenedores. No presentan riesgo de incendio por sí mismos.',
      large: 'Agua en diluvio para enfriar tanques. No presentan riesgo de incendio por sí mismos.',
      tank: 'AISLAR 300 m. Enfriar con agua desde distancia segura.',
    },
    ppe: {
      suit: 'Ropa de bombero estructural resistente al fuego (Nomex)',
      respirator: 'SCBA (equipo autónomo de respiración) presión positiva',
      gloves: 'Guantes resistentes al calor criogénico',
      boots: 'Botas de seguridad resistentes al calor',
    },
  },

  122: {
    number: 122,
    name: 'Gases Oxidantes (Incluye Gases Refrigerantes)',
    hazards: {
      fireExplosion: 'Son oxidantes; ACELERAN la combustión. Los contenedores pueden explotar cuando se calientan. El oxígeno gaseoso puede hacer que materiales normalmente no inflamables (telas, madera, aceite) ardan violentamente.',
      health: 'Los vapores pueden desplazar el oxígeno en ambientes cerrados causando asfixia (aunque el oxígeno puro no es tóxico, concentraciones elevadas aumentan el riesgo de incendio). Contacto con líquido criogénico puede causar quemaduras.',
    },
    publicSafety: {
      callNumber: '911 / ECU-911',
      isolationSmallSpill: 'Aislar al menos 25 m en todas las direcciones.',
      isolationLargeSpill: 'Aislar al menos 100 m en todas las direcciones.',
      fireIsolation: 'En incendio de tanques aislar 500 m.',
      protectiveActionSmall: 'Eliminar fuentes de ignición; ventilar el área.',
      protectiveActionLarge: 'Evacuar 200 m a favor del viento.',
    },
    emergencyResponse: {
      fire: [
        'NO usar material combustible (madera, papel) para controlar el fuego.',
        'Pequeño incendio: CO2 o agua en neblina.',
        'Gran incendio: Agua en neblina o diluvio de agua.',
        'Trasladar los contenedores del área de fuego si puede hacerse sin riesgo.',
        'Enfriar contenedores con agua.',
      ],
      spillLeak: [
        'ELIMINAR todas las fuentes de ignición y todo material combustible del área.',
        'No tocar ni caminar sobre material derramado (líquido criogénico).',
        'Detener la fuga si puede hacerse sin riesgo.',
        'Ventilar el área.',
        'No contaminar el suministro de agua.',
      ],
    },
    firstAid: [
      'Trasladar a la víctima a un área de aire fresco.',
      'Si no respira, administrar respiración artificial.',
      'Quemaduras criogénicas: lavar con agua tibia (NO caliente) durante 20 minutos.',
      'Trasladar al centro médico.',
    ],
    fireByType: {
      small: 'CO2 o agua en neblina. NO usar material combustible.',
      large: 'Agua en neblina o diluvio. NO usar material combustible.',
      tank: 'AISLAR 500 m. Enfriar contenedores con agua. Evacuar el área.',
    },
    ppe: {
      suit: 'Ropa de bombero estructural resistente al fuego (NO materiales combustibles)',
      respirator: 'SCBA (equipo autónomo de respiración) presión positiva',
      gloves: 'Guantes resistentes al calor (NO guantes de cuero)',
      boots: 'Botas de seguridad resistentes al calor',
    },
  },

  123: {
    number: 123,
    name: 'Gases Tóxicos y/o Corrosivos',
    hazards: {
      fireExplosion: 'Algunos pueden arder pero no se encienden fácilmente. Los contenedores pueden explotar cuando se calientan. El vapor es más pesado que el aire y puede desplazarse a considerables distancias.',
      health: 'MUY TÓXICO; puede ser fatal si se inhala. El contacto puede causar quemaduras severas en piel y ojos. El efecto puede no ser inmediato. La exposición a altas concentraciones puede ser fatal.',
    },
    publicSafety: {
      callNumber: '911 / ECU-911',
      isolationSmallSpill: 'Aislar al menos 100 m en todas las direcciones.',
      isolationLargeSpill: 'Aislar al menos 300 m en todas las direcciones.',
      fireIsolation: 'En incendio de tanques aislar 1600 m.',
      protectiveActionSmall: 'Evacuar 200 m a favor del viento.',
      protectiveActionLarge: 'Evacuar 1000 m a favor del viento.',
    },
    emergencyResponse: {
      fire: [
        'Usar SCBA presión positiva y ropa de protección química adecuada.',
        'Pequeño incendio: CO2, polvo seco o agua en neblina.',
        'Gran incendio: Agua en neblina, niebla o diluvio.',
        'NO apagar el fuego si la fuga no se puede detener.',
        'Retirar los contenedores del área si es posible.',
      ],
      spillLeak: [
        'NO ingresar al área sin equipo de protección respiratoria completo (SCBA).',
        'No tocar ni caminar sobre material derramado.',
        'Detener la fuga si puede hacerse sin riesgo.',
        'Ventilar las áreas cerradas.',
        'Prevenir la entrada en sumideros, sótanos u espacios confinados.',
        'Absorber con arena, tierra seca u otro material no combustible y transferir a contenedores.',
      ],
    },
    firstAid: [
      'Trasladar a la víctima fuera del área con SCBA y EPP completo.',
      'Llamar inmediatamente al 911 (ambulancia).',
      'Si no respira, administrar respiración artificial. NO usar el método boca a boca si se inhala el gas.',
      'Administrar oxígeno si está disponible.',
      'Quitar y aislar la ropa contaminada.',
      'Contacto con piel: lavar con agua y jabón durante 20 minutos.',
      'Contacto con ojos: irrigar con agua durante al menos 20 minutos.',
      'Trasladar URGENTEMENTE al hospital.',
    ],
    fireByType: {
      small: 'CO2, polvo seco o agua en neblina.',
      large: 'Agua en neblina, niebla o diluvio. NO apagar si la fuga no puede detenerse.',
      tank: 'AISLAR 1600 m. Considerar evacuación. Dejar quemar si es seguro.',
    },
    ppe: {
      suit: 'Traje de protección química nivel A (encapsulado)',
      respirator: 'SCBA (equipo autónomo de respiración) presión positiva',
      gloves: 'Guantes de protección química resistentes a gases tóxicos y corrosivos',
      boots: 'Botas de seguridad resistentes a químicos corrosivos',
    },
  },

  124: {
    number: 124,
    name: 'Gases Tóxicos y/o Corrosivos (Oxidantes)',
    hazards: {
      fireExplosion: 'Son oxidantes ACELERAN la combustión. Aunque no son inflamables, sus vapores pueden iniciar combustión de materiales combustibles. Los contenedores pueden explotar cuando se calientan.',
      health: 'MUY TÓXICO; puede ser fatal por inhalación, contacto cutáneo o ingestión. El contacto puede causar quemaduras severas. La exposición puede no producir efectos inmediatos.',
    },
    publicSafety: {
      callNumber: '911 / ECU-911',
      isolationSmallSpill: 'Aislar al menos 200 m en todas las direcciones.',
      isolationLargeSpill: 'Aislar al menos 500 m en todas las direcciones.',
      fireIsolation: 'En incendio de tanques aislar 1600 m.',
      protectiveActionSmall: 'Evacuar 400 m a favor del viento.',
      protectiveActionLarge: 'Evacuar 1000 m o más a favor del viento.',
    },
    emergencyResponse: {
      fire: [
        'Usar SCBA presión positiva y traje de protección química nivel A.',
        'NO usar material combustible para combatir el fuego.',
        'Usar agua en neblina, niebla o diluvio.',
        'Enfriar los contenedores con agua hasta mucho después de que el fuego se extinga.',
        'NO apagar el fuego si la fuga no se puede detener.',
      ],
      spillLeak: [
        'NO ingresar sin SCBA y traje de protección química nivel A.',
        'No tocar ni caminar sobre el material derramado.',
        'Mantener todo material combustible alejado del área de derrame.',
        'Detener la fuga si puede hacerse sin riesgo.',
        'Usar agua en neblina para reducir vapores.',
        'Prevenir la entrada en sumideros, sótanos u espacios confinados.',
      ],
    },
    firstAid: [
      'Trasladar a la víctima con EPP completo. NUNCA sin protección adecuada.',
      'Llamar al 911 inmediatamente.',
      'Si no respira: respiración artificial con máscara de bolsa-válvula. NO boca a boca.',
      'Quitar y aislar TODA la ropa contaminada (puede estar comprometida).',
      'Lavado cutáneo con agua durante mínimo 20 minutos.',
      'Lavado ocular con agua corriente durante mínimo 20 minutos.',
      'Trasladar URGENTEMENTE al hospital con ficha de datos de seguridad.',
    ],
    fireByType: {
      small: 'CO2 o agua en neblina. NO usar material combustible.',
      large: 'Agua en neblina o diluvio. NO apagar si la fuga no puede detenerse.',
      tank: 'AISLAR 1600 m. Considerar evacuación inmediata. Traje nivel A obligatorio.',
    },
    ppe: {
      suit: 'Traje de protección química nivel A (encapsulado)',
      respirator: 'SCBA (equipo autónomo de respiración) presión positiva',
      gloves: 'Guantes de protección química resistentes a oxidantes y gases tóxicos',
      boots: 'Botas de seguridad resistentes a químicos oxidantes',
    },
  },

  125: {
    number: 125,
    name: 'Gases Corrosivos',
    hazards: {
      fireExplosion: 'Algunos pueden arder pero no se encienden fácilmente. Los contenedores pueden explotar cuando se calientan. Algunos son oxidantes y pueden intensificar los incendios.',
      health: 'TÓXICO; la inhalación de vapores puede ser fatal. Corrosivo para las vías respiratorias, piel y ojos. El efecto puede ser inmediato (amoniaco, cloro) o retardado (fosgeno). Las concentraciones bajas pueden irritar; las altas pueden ser letales.',
    },
    publicSafety: {
      callNumber: '911 / ECU-911',
      isolationSmallSpill: 'Aislar al menos 100 m en todas las direcciones.',
      isolationLargeSpill: 'Aislar al menos 300 m en todas las direcciones.',
      fireIsolation: 'En incendio de tanques aislar 1600 m.',
      protectiveActionSmall: 'Evacuar 200 m a favor del viento.',
      protectiveActionLarge: 'Evacuar 800 m a favor del viento.',
    },
    emergencyResponse: {
      fire: [
        'Usar SCBA presión positiva y ropa de protección química.',
        'Pequeño incendio: CO2, agua en neblina o polvo seco.',
        'Gran incendio: Agua en neblina o diluvio de agua.',
        'Enfriar los contenedores con agua.',
        'Retirar los contenedores del área si es posible sin riesgo.',
      ],
      spillLeak: [
        'No ingresar sin SCBA y equipo de protección química adecuado.',
        'No tocar ni caminar sobre material derramado.',
        'Detener la fuga si puede hacerse sin riesgo.',
        'Usar agua en neblina para reducir vapores. Absorber con arena o tierra.',
        'Prevenir la entrada en sumideros o espacios confinados.',
        'No contaminar fuentes de agua.',
      ],
    },
    firstAid: [
      'Trasladar a la víctima a aire fresco con equipo de protección.',
      'Llamar al 911 (ambulancia) inmediatamente.',
      'Si no respira: respiración artificial. NO boca a boca si el gas fue inhalado.',
      'Si la respiración es difícil: administrar oxígeno.',
      'Quitar y aislar ropa contaminada.',
      'Lavado cutáneo con abundante agua y jabón durante 20 minutos.',
      'Lavado ocular con agua durante al menos 20 minutos.',
      'Trasladar urgentemente al hospital.',
    ],
    fireByType: {
      small: 'CO2, agua en neblina o polvo seco.',
      large: 'Agua en neblina o diluvio de agua.',
      tank: 'AISLAR 1600 m. Enfriar contenedores con agua. Evacuar el área.',
    },
    ppe: {
      suit: 'Traje de protección química resistente a corrosivos (nivel B mínimo)',
      respirator: 'SCBA (equipo autónomo de respiración) presión positiva',
      gloves: 'Guantes de protección química resistentes a gases corrosivos',
      boots: 'Botas de seguridad resistentes a corrosivos',
    },
  },

  126: {
    number: 126,
    name: 'Gases Comprimidos (No Especificado en Otras Guías)',
    hazards: {
      fireExplosion: 'Los contenedores pueden explotar cuando se calientan. Algunos gases pueden ser inflamables; verificar etiqueta del producto.',
      health: 'Los vapores pueden desplazar el oxígeno causando asfixia en espacios confinados. El contacto con el gas licuado puede causar quemaduras criogénicas.',
    },
    publicSafety: {
      callNumber: '911 / ECU-911',
      isolationSmallSpill: 'Aislar al menos 25 m en todas las direcciones.',
      isolationLargeSpill: 'Aislar al menos 50 m en todas las direcciones.',
      fireIsolation: 'En incendio de tanques aislar 300 m.',
      protectiveActionSmall: 'Ventilar el área.',
      protectiveActionLarge: 'Evacuar si hay acumulación.',
    },
    emergencyResponse: {
      fire: [
        'Pequeño incendio: CO2 o polvo seco.',
        'Gran incendio: Agua en neblina o diluvio de agua.',
        'Enfriar contenedores con agua.',
        'Retirar contenedores del área si puede hacerse sin riesgo.',
      ],
      spillLeak: [
        'No tocar ni caminar sobre el material derramado.',
        'Detener la fuga si puede hacerse sin riesgo.',
        'Ventilar el área cerrada.',
        'Prevenir la entrada en espacios confinados.',
      ],
    },
    firstAid: [
      'Trasladar a la víctima a aire fresco.',
      'Si no respira: respiración artificial.',
      'Si la respiración es difícil: administrar oxígeno.',
      'Quemaduras criogénicas: lavar con agua tibia durante 20 minutos.',
      'Trasladar al centro médico.',
    ],
    fireByType: {
      small: 'CO2 o polvo seco.',
      large: 'Agua en neblina o diluvio de agua.',
      tank: 'AISLAR 300 m. Enfriar contenedores con agua.',
    },
    ppe: {
      suit: 'Ropa de bombero estructural resistente al fuego',
      respirator: 'SCBA si el gas puede desplazar el oxígeno; máscara filtrante si el gas es conocido',
      gloves: 'Guantes resistentes al calor',
      boots: 'Botas de seguridad antiestáticas',
    },
  },

  127: {
    number: 127,
    name: 'Líquidos Inflamables (Polares / Miscibles en Agua)',
    hazards: {
      fireExplosion: 'INFLAMABLES; los vapores pueden formar mezclas explosivas con el aire. Los vapores pueden desplazarse a considerable distancia hasta una fuente de ignición. La mayoría son más ligeros que el agua. Los envases pueden explotar cuando se calientan.',
      health: 'La inhalación o el contacto con el material puede irritar o quemar la piel y los ojos. El fuego puede producir gases irritantes, corrosivos o tóxicos. Los vapores pueden causar mareo o sofocación.',
    },
    publicSafety: {
      callNumber: '911 / ECU-911',
      isolationSmallSpill: 'Aislar al menos 50 m en todas las direcciones.',
      isolationLargeSpill: 'Aislar al menos 100 m en todas las direcciones.',
      fireIsolation: 'En incendio de tanques aislar 500 m.',
      protectiveActionSmall: 'Eliminar fuentes de ignición.',
      protectiveActionLarge: 'Evacuar 300 m a favor del viento.',
    },
    emergencyResponse: {
      fire: [
        'ELIMINAR todas las fuentes de ignición.',
        'Pequeño incendio: CO2, polvo seco, polvo ABC o espuma resistente al alcohol.',
        'Gran incendio: Agua en diluvio, niebla o espuma resistente al alcohol.',
        'NO usar chorro de agua directo (puede dispersar el incendio).',
        'Enfriar los contenedores con agua.',
        'Retirar los contenedores del área si puede hacerse sin riesgo.',
      ],
      spillLeak: [
        'ELIMINAR todas las fuentes de ignición (no fumar, no usar bengalas ni llamas).',
        'No tocar ni caminar sobre material derramado.',
        'Detener la fuga si puede hacerse sin riesgo.',
        'Prevenir la entrada en sumideros, sótanos u espacios confinados.',
        'Recoger con absorbente inerte (arena, tierra, tierra diatomácea) y transferir a contenedores.',
        'Usar agua en neblina para reducir los vapores.',
      ],
    },
    firstAid: [
      'Trasladar a la víctima a un área de aire fresco.',
      'Llamar al 911 si hay sintomatología.',
      'Si no respira: respiración artificial.',
      'Si la respiración es difícil: administrar oxígeno.',
      'Quitar y aislar la ropa contaminada.',
      'Contacto con piel: lavar con agua corriente y jabón durante 20 minutos.',
      'Contacto con ojos: irrigar con agua a temperatura ambiente durante al menos 20 minutos.',
      'Si se ingirió y la víctima está consciente, dar agua para diluir. NO inducir el vómito.',
      'Trasladar al centro médico.',
    ],
    fireByType: {
      small: 'CO2, polvo seco, polvo ABC o espuma resistente al alcohol.',
      large: 'Espuma resistente al alcohol o agua en diluvio. NO chorro directo.',
      tank: 'AISLAR 500 m. Enfriar contenedores con agua.',
    },
    ppe: {
      suit: 'Ropa de bombero estructural resistente al fuego',
      respirator: 'SCBA (equipo autónomo de respiración) presión positiva',
      gloves: 'Guantes resistentes al calor y a solventes polares',
      boots: 'Botas de seguridad antiestáticas resistentes al calor',
    },
  },

  128: {
    number: 128,
    name: 'Líquidos Inflamables (No Polares / No Miscibles en Agua)',
    hazards: {
      fireExplosion: 'INFLAMABLES; los vapores pueden formar mezclas explosivas con el aire. Los vapores son más pesados que el aire y pueden desplazarse a distancias considerables hasta una fuente de ignición y causar retorno del fuego. La mayoría flotan sobre el agua y pueden re-encenderse.',
      health: 'La inhalación o el contacto puede irritar la piel, ojos y vías respiratorias. El fuego puede producir gases irritantes o tóxicos. Los vapores pueden causar mareo o sofocación. Puede ser nocivo si se ingiere.',
    },
    publicSafety: {
      callNumber: '911 / ECU-911',
      isolationSmallSpill: 'Aislar al menos 50 m en todas las direcciones.',
      isolationLargeSpill: 'Aislar al menos 150 m en todas las direcciones.',
      fireIsolation: 'En incendio de tanques aislar 500 m.',
      protectiveActionSmall: 'Eliminar fuentes de ignición; ventilar.',
      protectiveActionLarge: 'Evacuar 300 m a favor del viento.',
    },
    emergencyResponse: {
      fire: [
        'ELIMINAR todas las fuentes de ignición.',
        'Pequeño incendio: CO2, polvo seco o espuma.',
        'Gran incendio: Agua en diluvio, niebla o espuma.',
        'NO usar chorro de agua directo sobre el líquido en llamas.',
        'Enfriar los contenedores con agua.',
        'Retirar los contenedores del área si puede hacerse sin riesgo.',
        'Derrames en agua: detener el fuego si es posible; cortar el flujo del líquido.',
      ],
      spillLeak: [
        'ELIMINAR todas las fuentes de ignición en el área.',
        'No tocar ni caminar sobre material derramado.',
        'Detener la fuga si puede hacerse sin riesgo.',
        'Usar agua en neblina para reducir vapores; NO usar chorro directo.',
        'Prevenir la entrada en sumideros, sótanos u otros espacios confinados.',
        'Recoger con absorbente inerte seco (tierra, arena) y colocar en contenedores de desecho.',
        'NO contaminar cuerpos de agua superficiales ni el alcantarillado.',
      ],
    },
    firstAid: [
      'Trasladar a la víctima a un área de aire fresco.',
      'Llamar al 911 si hay sintomatología.',
      'Si no respira: respiración artificial.',
      'Si la respiración es difícil: administrar oxígeno.',
      'Quitar y aislar la ropa contaminada.',
      'Contacto con piel: lavar con agua corriente y jabón durante 20 minutos.',
      'Contacto con ojos: irrigar con agua a temperatura ambiente durante al menos 20 minutos.',
      'NO inducir el vómito: riesgo de aspiración a pulmones.',
      'Trasladar al centro médico.',
    ],
    fireByType: {
      small: 'CO2, polvo seco o espuma.',
      large: 'Espuma o agua en diluvio. NO chorro directo.',
      tank: 'AISLAR 500 m. Enfriar contenedores con agua.',
    },
    ppe: {
      suit: 'Ropa de bombero estructural resistente al fuego',
      respirator: 'SCBA (equipo autónomo de respiración) presión positiva',
      gloves: 'Guantes resistentes al calor e hidrocarburos',
      boots: 'Botas de seguridad antiestáticas resistentes al calor',
    },
  },

  129: {
    number: 129,
    name: 'Líquidos Inflamables (Polares / Miscibles en Agua / Nocivos)',
    hazards: {
      fireExplosion: 'INFLAMABLES; los vapores pueden formar mezclas explosivas con el aire. Los vapores son más pesados que el aire. La mayoría son miscibles en agua. Los contenedores pueden explotar cuando se calientan.',
      health: 'La inhalación, contacto con la piel o la ingestión pueden ser NOCIVOS. El fuego puede producir gases irritantes, corrosivos o tóxicos. La exposición prolongada puede afectar el hígado, riñones y sistema nervioso.',
    },
    publicSafety: {
      callNumber: '911 / ECU-911',
      isolationSmallSpill: 'Aislar al menos 50 m en todas las direcciones.',
      isolationLargeSpill: 'Aislar al menos 100 m en todas las direcciones.',
      fireIsolation: 'En incendio de tanques aislar 500 m.',
      protectiveActionSmall: 'Eliminar fuentes de ignición.',
      protectiveActionLarge: 'Evacuar 300 m a favor del viento.',
    },
    emergencyResponse: {
      fire: [
        'ELIMINAR todas las fuentes de ignición.',
        'Pequeño incendio: CO2, polvo seco o espuma resistente al alcohol.',
        'Gran incendio: Espuma resistente al alcohol o agua en diluvio.',
        'NO usar chorro de agua directo (puede propagar el líquido).',
        'Enfriar los contenedores con agua.',
      ],
      spillLeak: [
        'ELIMINAR todas las fuentes de ignición.',
        'No tocar ni caminar sobre material derramado.',
        'Detener la fuga si puede hacerse sin riesgo.',
        'Prevenir la entrada en sumideros, drenajes y cuerpos de agua.',
        'Recoger con arena u otro material absorbente no combustible.',
        'Usar agua en neblina para reducir vapores.',
      ],
    },
    firstAid: [
      'Trasladar a la víctima a un área de aire fresco.',
      'Llamar al 911.',
      'Si no respira: respiración artificial.',
      'Si la respiración es difícil: administrar oxígeno.',
      'Quitar y aislar la ropa contaminada.',
      'Lavado cutáneo con agua y jabón durante 20 minutos.',
      'Lavado ocular con agua durante al menos 20 minutos.',
      'Si hubo ingestión: NO inducir el vómito. Dar agua si está consciente.',
      'Trasladar al centro médico.',
    ],
    fireByType: {
      small: 'CO2, polvo seco o espuma resistente al alcohol.',
      large: 'Espuma resistente al alcohol o agua en diluvio.',
      tank: 'AISLAR 500 m. Enfriar contenedores con agua.',
    },
    ppe: {
      suit: 'Ropa de bombero estructural resistente al fuego',
      respirator: 'SCBA (equipo autónomo de respiración) presión positiva',
      gloves: 'Guantes resistentes al calor y a solventes orgánicos',
      boots: 'Botas de seguridad antiestáticas resistentes al calor',
    },
  },

  130: {
    number: 130,
    name: 'Líquidos Inflamables (No Polares / No Miscibles en Agua / Nocivos)',
    hazards: {
      fireExplosion: 'INFLAMABLES; los vapores pueden formar mezclas explosivas con el aire. Los vapores son más pesados que el aire. La mayoría flotan sobre el agua. Los contenedores pueden explotar cuando se calientan.',
      health: 'La inhalación, contacto cutáneo o la ingestión pueden ser NOCIVOS. Pueden causar irritación de las vías respiratorias, piel y ojos. Algunos compuestos aromáticos (benceno, tolueno, xileno) pueden ser carcinogénicos o neurotóxicos.',
    },
    publicSafety: {
      callNumber: '911 / ECU-911',
      isolationSmallSpill: 'Aislar al menos 50 m en todas las direcciones.',
      isolationLargeSpill: 'Aislar al menos 150 m en todas las direcciones.',
      fireIsolation: 'En incendio de tanques aislar 500 m.',
      protectiveActionSmall: 'Eliminar fuentes de ignición; ventilar.',
      protectiveActionLarge: 'Evacuar 300 m a favor del viento.',
    },
    emergencyResponse: {
      fire: [
        'ELIMINAR todas las fuentes de ignición.',
        'Pequeño incendio: CO2, polvo seco o espuma.',
        'Gran incendio: Agua en diluvio, niebla o espuma.',
        'NO usar chorro de agua directo sobre el líquido en llamas.',
        'Retirar los contenedores del área si puede hacerse sin riesgo.',
        'Enfriar los contenedores con agua.',
      ],
      spillLeak: [
        'ELIMINAR todas las fuentes de ignición en el área.',
        'No tocar ni caminar sobre material derramado.',
        'Detener la fuga si puede hacerse sin riesgo.',
        'Usar agua en neblina para reducir vapores; NO chorro directo.',
        'Prevenir la entrada en sumideros, sótanos y cuerpos de agua.',
        'Recoger con material absorbente inerte (tierra, arena) y depositar en contenedores.',
      ],
    },
    firstAid: [
      'Trasladar a la víctima a un área de aire fresco.',
      'Llamar al 911.',
      'Si no respira: respiración artificial.',
      'Si la respiración es difícil: administrar oxígeno.',
      'Quitar y aislar la ropa contaminada.',
      'Contacto con piel: lavar con agua corriente y jabón durante 20 minutos.',
      'Contacto con ojos: irrigar con agua durante al menos 20 minutos.',
      'NO inducir el vómito (riesgo de neumonitis por aspiración).',
      'Trasladar al centro médico.',
    ],
    fireByType: {
      small: 'CO2, polvo seco o espuma.',
      large: 'Espuma o agua en diluvio. NO chorro directo.',
      tank: 'AISLAR 500 m. Enfriar contenedores con agua.',
    },
    ppe: {
      suit: 'Ropa de bombero estructural resistente al fuego',
      respirator: 'SCBA (equipo autónomo de respiración) presión positiva',
      gloves: 'Guantes resistentes al calor e hidrocarburos aromáticos',
      boots: 'Botas de seguridad antiestáticas resistentes al calor',
    },
  },

  131: {
    number: 131,
    name: 'Líquidos Inflamables - Tóxicos',
    hazards: {
      fireExplosion: 'INFLAMABLES; los vapores pueden formar mezclas explosivas con el aire. El incendio puede producir gases muy tóxicos y/o irritantes. Los contenedores pueden explotar cuando se calientan.',
      health: 'TÓXICO; puede ser fatal si se inhala, ingiere o absorbe por la piel. El contacto puede causar quemaduras cutáneas y oculares. El humo del incendio puede ser muy tóxico.',
    },
    publicSafety: {
      callNumber: '911 / ECU-911',
      isolationSmallSpill: 'Aislar al menos 50 m en todas las direcciones.',
      isolationLargeSpill: 'Aislar al menos 200 m en todas las direcciones.',
      fireIsolation: 'En incendio de tanques aislar 800 m.',
      protectiveActionSmall: 'Eliminar fuentes de ignición; usar EPP.',
      protectiveActionLarge: 'Evacuar 500 m a favor del viento.',
    },
    emergencyResponse: {
      fire: [
        'Usar SCBA presión positiva y ropa de protección química.',
        'Pequeño incendio: CO2, polvo seco o espuma resistente al alcohol.',
        'Gran incendio: Agua en diluvio, niebla o espuma.',
        'NO usar chorro de agua directo.',
        'Enfriar los contenedores con agua.',
      ],
      spillLeak: [
        'ELIMINAR todas las fuentes de ignición.',
        'No ingresar sin SCBA y EPP adecuado.',
        'No tocar ni caminar sobre material derramado.',
        'Detener la fuga si puede hacerse sin riesgo.',
        'Prevenir la entrada en sumideros, sótanos y drenajes.',
        'Recoger con absorbente inerte no combustible y depositar en contenedores.',
        'Usar agua en neblina para reducir vapores.',
      ],
    },
    firstAid: [
      'Trasladar a la víctima a un área de aire fresco con EPP adecuado.',
      'Llamar al 911 URGENTE.',
      'Si no respira: respiración artificial. NO boca a boca.',
      'Si la respiración es difícil: administrar oxígeno.',
      'Quitar INMEDIATAMENTE toda la ropa contaminada.',
      'Lavado cutáneo con agua corriente durante mínimo 20 minutos.',
      'Lavado ocular con agua durante mínimo 20 minutos.',
      'NO inducir el vómito.',
      'Trasladar URGENTEMENTE al hospital con la ficha de seguridad.',
    ],
    fireByType: {
      small: 'CO2, polvo seco o espuma resistente al alcohol.',
      large: 'Agua en diluvio, niebla o espuma.',
      tank: 'AISLAR 800 m. Enfriar contenedores con agua.',
    },
    ppe: {
      suit: 'Traje de protección química resistente a tóxicos (nivel B o superior)',
      respirator: 'SCBA (equipo autónomo de respiración) presión positiva',
      gloves: 'Guantes de protección química resistentes a tóxicos',
      boots: 'Botas de seguridad antiestáticas resistentes a tóxicos',
    },
  },

  132: {
    number: 132,
    name: 'Líquidos Inflamables - Corrosivos',
    hazards: {
      fireExplosion: 'INFLAMABLES; los vapores pueden formar mezclas explosivas con el aire. El incendio puede producir gases corrosivos y/o tóxicos. Los contenedores pueden explotar cuando se calientan.',
      health: 'CORROSIVO; causa quemaduras en piel y ojos. Los vapores pueden ser muy irritantes y corrosivos para las vías respiratorias. Puede ser tóxico si se inhala o ingiere.',
    },
    publicSafety: {
      callNumber: '911 / ECU-911',
      isolationSmallSpill: 'Aislar al menos 50 m en todas las direcciones.',
      isolationLargeSpill: 'Aislar al menos 200 m en todas las direcciones.',
      fireIsolation: 'En incendio de tanques aislar 500 m.',
      protectiveActionSmall: 'Evacuar 100 m; usar EPP.',
      protectiveActionLarge: 'Evacuar 300 m a favor del viento.',
    },
    emergencyResponse: {
      fire: [
        'Usar SCBA presión positiva y ropa resistente a químicos.',
        'Pequeño incendio: CO2, polvo seco o espuma.',
        'Gran incendio: Agua en diluvio, niebla o espuma.',
        'NO usar chorro de agua directo.',
        'Enfriar los contenedores con agua.',
        'Retirar los contenedores si puede hacerse sin riesgo.',
      ],
      spillLeak: [
        'ELIMINAR todas las fuentes de ignición.',
        'No ingresar sin EPP completo (guantes, protección ocular, respirador).',
        'No tocar ni caminar sobre material derramado.',
        'Detener la fuga si puede hacerse sin riesgo.',
        'Contener el derrame con barreras de arena o tierra.',
        'Recoger con absorbente inerte y depositar en contenedores resistentes a corrosivos.',
        'NO lavar con agua (puede generar reacción exotérmica en algunos casos).',
      ],
    },
    firstAid: [
      'Trasladar a la víctima a un área de aire fresco.',
      'Llamar al 911 urgente.',
      'Si no respira: respiración artificial.',
      'Quitar INMEDIATAMENTE la ropa contaminada (puede continuar quemando).',
      'Lavado cutáneo con abundante agua corriente durante mínimo 20 minutos.',
      'Lavado ocular con agua corriente durante mínimo 20 minutos.',
      'NO inducir el vómito (corrosivo para el esófago).',
      'Trasladar al hospital urgente. Llevar la ficha de seguridad.',
    ],
    fireByType: {
      small: 'CO2, polvo seco o espuma.',
      large: 'Agua en diluvio, niebla o espuma.',
      tank: 'AISLAR 500 m. Enfriar contenedores con agua.',
    },
    ppe: {
      suit: 'Traje de protección química resistente a corrosivos (nivel B mínimo)',
      respirator: 'SCBA (equipo autónomo de respiración) presión positiva',
      gloves: 'Guantes de protección química resistentes a corrosivos',
      boots: 'Botas de seguridad resistentes a corrosivos',
    },
  },

  133: {
    number: 133,
    name: 'Sólidos Inflamables',
    hazards: {
      fireExplosion: 'INFLAMABLE; puede encenderse por fricción, calor o chispa. El polvo o el polvo fino puede formar mezclas explosivas con el aire. El fuego puede producir gases irritantes, corrosivos o tóxicos. Puede re-encenderse después de que el fuego se extinga.',
      health: 'El contacto puede causar irritación de la piel, ojos y vías respiratorias. La inhalación del polvo puede ser dañina. El fuego puede producir humos tóxicos.',
    },
    publicSafety: {
      callNumber: '911 / ECU-911',
      isolationSmallSpill: 'Aislar al menos 25 m en todas las direcciones.',
      isolationLargeSpill: 'Aislar al menos 50 m en todas las direcciones.',
      fireIsolation: 'En incendio de tanques aislar 300 m.',
      protectiveActionSmall: 'Eliminar fuentes de ignición.',
      protectiveActionLarge: 'Evacuar 100 m a favor del viento.',
    },
    emergencyResponse: {
      fire: [
        'Pequeño incendio: Agua, polvo seco, CO2 o arena.',
        'Gran incendio: Agua en diluvio o niebla.',
        'No usar agua si produce una reacción peligrosa (verificar en SDS).',
        'Mover los contenedores del área de incendio si puede hacerse sin riesgo.',
        'Enfriar con agua los contenedores expuestos.',
        'Prevenir la escorrentía de agua contaminada.',
      ],
      spillLeak: [
        'ELIMINAR todas las fuentes de ignición.',
        'No tocar ni caminar sobre material derramado.',
        'No generar polvo (no barrer en seco).',
        'Recoger con técnicas de bajo levantamiento de polvo y colocar en contenedores.',
        'Usar agua para suprimir el polvo si es apropiado según la SDS.',
        'Cubrir con material húmedo para suprimir el polvo.',
      ],
    },
    firstAid: [
      'Trasladar a la víctima a un área de aire fresco.',
      'Si no respira: respiración artificial.',
      'Si la respiración es difícil: administrar oxígeno.',
      'Quitar la ropa contaminada.',
      'Contacto con piel: lavar con agua y jabón durante 20 minutos.',
      'Contacto con ojos: irrigar con agua durante al menos 20 minutos.',
      'Trasladar al centro médico si hay sintomatología.',
    ],
    fireByType: {
      small: 'Agua, polvo seco, CO2 o arena.',
      large: 'Agua en diluvio o niebla.',
      tank: 'AISLAR 300 m. Enfriar contenedores con agua.',
    },
    ppe: {
      suit: 'Ropa de bombero estructural resistente al fuego',
      respirator: 'SCBA (equipo autónomo de respiración) si hay humos tóxicos',
      gloves: 'Guantes resistentes al calor',
      boots: 'Botas de seguridad resistentes al calor',
    },
  },

  137: {
    number: 137,
    name: 'Ácidos Inorgánicos Fuertes y/o Corrosivos',
    hazards: {
      fireExplosion: 'No son combustibles, pero son OXIDANTES fuertes y pueden iniciar o intensificar el fuego. Reaccionan violentamente con metales, generando hidrógeno inflamable. El contacto con agua puede generar calor intenso y proyecciones de ácido.',
      health: 'TÓXICO y CORROSIVO; puede ser fatal si se inhala, ingiere o entra en contacto con la piel. Produce quemaduras severas en piel, ojos y tracto respiratorio. Los vapores pueden causar irritación severa del tracto respiratorio. El ácido sulfúrico concentrado es higroscópico y altamente exotérmico al contacto con agua.',
    },
    publicSafety: {
      callNumber: '911 / ECU-911',
      isolationSmallSpill: 'Aislar al menos 50 m en todas las direcciones.',
      isolationLargeSpill: 'Aislar al menos 300 m en todas las direcciones.',
      fireIsolation: 'En incendio de tanques aislar 800 m.',
      protectiveActionSmall: 'Usar EPP completo; evacuar 100 m.',
      protectiveActionLarge: 'Evacuar 500 m a favor del viento; usar SCBA.',
    },
    emergencyResponse: {
      fire: [
        'Usar SCBA presión positiva y ropa de protección química completa.',
        'Pequeño incendio: CO2 o polvo seco.',
        'Gran incendio: Agua en diluvio. PRECAUCIÓN: puede generar proyecciones al contacto con ácidos concentrados.',
        'NO usar chorro de agua directo sobre ácido concentrado (reacción exotérmica violenta).',
        'Enfriar los contenedores con agua pulverizada desde distancia segura.',
        'Retirar todos los contenedores del área si puede hacerse sin riesgo.',
      ],
      spillLeak: [
        'Usar SCBA y traje de protección química resistente a ácidos.',
        'No tocar ni caminar sobre material derramado.',
        'No agregar agua al ácido concentrado; pequeñas cantidades pueden dilutirse agregando ácido al agua.',
        'Detener la fuga si puede hacerse sin riesgo.',
        'Contener el derrame con arena o tierra seca (NO con materiales reactivos).',
        'Neutralizar LENTAMENTE con bicarbonato de sodio, carbonato de sodio o cal apagada.',
        'Prevenir la entrada en drenajes y cuerpos de agua.',
        'Recoger en contenedores resistentes a corrosivos.',
      ],
    },
    firstAid: [
      'Retirar a la víctima del área con EPP completo.',
      'Llamar al 911 URGENTE.',
      'Si no respira: respiración artificial con máscara. NO boca a boca.',
      'Quitar INMEDIATAMENTE la ropa y zapatos contaminados.',
      'Lavado cutáneo con abundante agua corriente durante MÍNIMO 20 minutos.',
      'Lavado ocular con agua limpia durante MÍNIMO 20 minutos. NO frotar.',
      'Si se ingirió: NO inducir el vómito. Dar agua (si está consciente). NO dar leche ni bicarbonato.',
      'Trasladar URGENTEMENTE al hospital. Llevar la etiqueta del producto.',
    ],
    fireByType: {
      small: 'CO2 o polvo seco. PRECAUCIÓN: NO agua directa sobre ácido concentrado.',
      large: 'Agua en diluvio desde distancia segura. Riesgo de proyecciones.',
      tank: 'AISLAR 800 m. Enfriar con agua pulverizada desde distancia segura.',
    },
    ppe: {
      suit: 'Traje de protección química resistente a ácidos (nivel A o B)',
      respirator: 'SCBA (equipo autónomo de respiración) presión positiva',
      gloves: 'Guantes de protección química resistentes a ácidos fuertes',
      boots: 'Botas de seguridad resistentes a ácidos',
    },
  },

  140: {
    number: 140,
    name: 'Oxidantes',
    hazards: {
      fireExplosion: 'Pueden INTENSIFICAR EL FUEGO; el calor puede causar incendio espontáneo de materiales combustibles. El contacto con combustibles (madera, papel, aceite, ropa) puede causar ignición inmediata. Algunos pueden reaccionar explosivamente al contacto con calor, choque o contaminación. Los contenedores pueden explotar cuando se calientan.',
      health: 'La inhalación puede irritar o quemar el tracto respiratorio. El contacto puede causar quemaduras en piel y ojos. El fuego puede producir gases irritantes y corrosivos.',
    },
    publicSafety: {
      callNumber: '911 / ECU-911',
      isolationSmallSpill: 'Aislar al menos 25 m en todas las direcciones.',
      isolationLargeSpill: 'Aislar al menos 100 m en todas las direcciones.',
      fireIsolation: 'En incendio de tanques aislar 500 m.',
      protectiveActionSmall: 'Mantener materiales combustibles alejados.',
      protectiveActionLarge: 'Evacuar 300 m a favor del viento.',
    },
    emergencyResponse: {
      fire: [
        'NO usar material combustible para sofocar el incendio.',
        'Pequeño incendio: CO2 o agua en neblina.',
        'Gran incendio: Agua en diluvio o neblina. Los oxidantes pueden re-encenderse.',
        'Enfriar los contenedores con agua.',
        'Retirar los contenedores del área si puede hacerse sin riesgo.',
        'Mantener materiales combustibles lejos del área.',
      ],
      spillLeak: [
        'No tocar ni caminar sobre material derramado.',
        'MANTENER ALEJADOS todos los materiales combustibles del área del derrame.',
        'Detener la fuga si puede hacerse sin riesgo.',
        'Usar agua en neblina para reducir vapores.',
        'NO contaminar el suministro de agua.',
        'Pequeño derrame seco: cubrir con arena húmeda y transferir a contenedores.',
        'Grande derrame: diluir con abundante agua y drenar si cumple con normativa ambiental.',
      ],
    },
    firstAid: [
      'Trasladar a la víctima a un área de aire fresco.',
      'Llamar al 911 si hay sintomatología.',
      'Si no respira: respiración artificial.',
      'Si la respiración es difícil: administrar oxígeno.',
      'Quitar la ropa contaminada.',
      'Contacto con piel: lavar con agua durante 20 minutos.',
      'Contacto con ojos: irrigar con agua durante al menos 20 minutos.',
      'Si se ingirió: enjuagar la boca. NO inducir el vómito. Dar agua si está consciente.',
      'Trasladar al centro médico.',
    ],
    fireByType: {
      small: 'CO2 o agua en neblina. NO usar material combustible.',
      large: 'Agua en diluvio o neblina. Los oxidantes pueden re-encenderse.',
      tank: 'AISLAR 500 m. Enfriar contenedores con agua. Mantener materiales combustibles alejados.',
    },
    ppe: {
      suit: 'Ropa de bombero estructural resistente al fuego (NO materiales combustibles)',
      respirator: 'SCBA (equipo autónomo de respiración) presión positiva',
      gloves: 'Guantes resistentes al calor (NO guantes de cuero)',
      boots: 'Botas de seguridad resistentes al calor',
    },
  },

  143: {
    number: 143,
    name: 'Oxidantes - Líquidos (Inestables)',
    hazards: {
      fireExplosion: 'Pueden INTENSIFICAR EL FUEGO o EXPLOTAR cuando se calientan o se contaminan. El contacto con materiales combustibles puede causar incendio. Son INESTABLES: pueden descomponerse explosivamente por calor, contaminación, impacto o vibración.',
      health: 'CORROSIVO; puede ser fatal si se inhala, ingiere o entra en contacto con la piel. Produce quemaduras severas en piel y ojos. Los vapores pueden causar irritación severa del tracto respiratorio.',
    },
    publicSafety: {
      callNumber: '911 / ECU-911',
      isolationSmallSpill: 'Aislar al menos 50 m en todas las direcciones.',
      isolationLargeSpill: 'Aislar al menos 200 m en todas las direcciones.',
      fireIsolation: 'En incendio de tanques aislar 800 m. Evacuar zona circular.',
      protectiveActionSmall: 'Usar EPP; evacuar 100 m.',
      protectiveActionLarge: 'Evacuar 500 m a favor del viento.',
    },
    emergencyResponse: {
      fire: [
        'Usar SCBA presión positiva y ropa de protección química.',
        'Pequeño incendio: CO2 o agua en neblina.',
        'Gran incendio: Agua en diluvio. PRECAUCIÓN: puede intensificar el fuego con oxidantes.',
        'NO usar material combustible para sofocar el fuego.',
        'Enfriar los contenedores con agua.',
        'Si los contenedores se ven decolorados o deformados: RETIRARSE INMEDIATAMENTE.',
      ],
      spillLeak: [
        'No tocar ni caminar sobre material derramado.',
        'MANTENER ALEJADOS todos los materiales combustibles.',
        'Detener la fuga si puede hacerse sin riesgo.',
        'No agregar agua directamente al peróxido concentrado.',
        'Diluir lentamente con agua fría si hay fuga pequeña.',
        'Prevenir la entrada en drenajes y cuerpos de agua.',
        'Recoger en contenedores de vidrio o HDPE. No usar contenedores metálicos ni de aluminio.',
      ],
    },
    firstAid: [
      'Trasladar a la víctima a un área de aire fresco.',
      'Llamar al 911 URGENTE.',
      'Si no respira: respiración artificial.',
      'Quitar INMEDIATAMENTE la ropa contaminada.',
      'Lavado cutáneo con abundante agua durante MÍNIMO 20 minutos.',
      'Lavado ocular con agua durante MÍNIMO 20 minutos. NO frotar.',
      'NO inducir el vómito.',
      'Trasladar URGENTEMENTE al hospital.',
    ],
    fireByType: {
      small: 'CO2 o agua en neblina.',
      large: 'Agua en diluvio. PRECAUCIÓN: puede intensificar el fuego.',
      tank: 'AISLAR 800 m. Retirarse si los contenedores se ven deformados. Enfriar con agua.',
    },
    ppe: {
      suit: 'Traje de protección química resistente a oxidantes (nivel B mínimo)',
      respirator: 'SCBA (equipo autónomo de respiración) presión positiva',
      gloves: 'Guantes de protección química resistentes a oxidantes',
      boots: 'Botas de seguridad resistentes a químicos oxidantes',
    },
  },

  151: {
    number: 151,
    name: 'Sustancias Tóxicas (No Combustibles)',
    hazards: {
      fireExplosion: 'Pueden ser combustibles pero no se encienden fácilmente. Cuando se calientan, los contenedores pueden explotar. El fuego puede producir gases irritantes, corrosivos o tóxicos.',
      health: 'TÓXICO; puede ser fatal si se inhala, ingiere o absorbe por la piel. El contacto puede causar quemaduras en piel y ojos. La inhalación de polvo, vapor o neblina puede ser dañina. La exposición puede no producir síntomas inmediatos.',
    },
    publicSafety: {
      callNumber: '911 / ECU-911',
      isolationSmallSpill: 'Aislar al menos 25 m en todas las direcciones.',
      isolationLargeSpill: 'Aislar al menos 100 m en todas las direcciones.',
      fireIsolation: 'En incendio de tanques aislar 300 m.',
      protectiveActionSmall: 'Usar EPP adecuado.',
      protectiveActionLarge: 'Evacuar 300 m a favor del viento.',
    },
    emergencyResponse: {
      fire: [
        'Usar SCBA presión positiva y ropa protectora adecuada.',
        'Pequeño incendio: CO2, polvo seco o agua en neblina.',
        'Gran incendio: Agua en diluvio, niebla o espuma.',
        'Enfriar los contenedores con agua.',
        'Retirar los contenedores si puede hacerse sin riesgo.',
      ],
      spillLeak: [
        'No tocar ni caminar sobre material derramado.',
        'Detener la fuga si puede hacerse sin riesgo.',
        'Prevenir la entrada en sumideros, sótanos y espacios confinados.',
        'Absorber con material inerte (vermiculita, tierra diatomácea) y depositar en contenedor.',
        'No generar polvo.',
        'No contaminar el suministro de agua.',
      ],
    },
    firstAid: [
      'Trasladar a la víctima a un área de aire fresco.',
      'Llamar al 911.',
      'Si no respira: respiración artificial con protección.',
      'Si la respiración es difícil: administrar oxígeno.',
      'Quitar y aislar la ropa contaminada.',
      'Contacto con piel: lavar con agua y jabón durante 20 minutos.',
      'Contacto con ojos: irrigar con agua durante al menos 20 minutos.',
      'Si se ingirió: enjuagar la boca con agua. NO inducir el vómito.',
      'Trasladar al hospital. Línea gratuita CIATOX (MSP): 1800 836366.',
    ],
    fireByType: {
      small: 'CO2, polvo seco o agua en neblina.',
      large: 'Agua en diluvio, niebla o espuma.',
      tank: 'AISLAR 300 m. Enfriar contenedores con agua.',
    },
    ppe: {
      suit: 'Ropa de protección química resistente a tóxicos',
      respirator: 'SCBA (equipo autónomo de respiración) presión positiva',
      gloves: 'Guantes de protección química resistentes a tóxicos',
      boots: 'Botas de seguridad resistentes a tóxicos',
    },
  },

  153: {
    number: 153,
    name: 'Sustancias Tóxicas y/o Corrosivas (Combustibles)',
    hazards: {
      fireExplosion: 'COMBUSTIBLE. El incendio puede producir gases irritantes, corrosivos o tóxicos. Los contenedores pueden explotar cuando se calientan. Los vapores pueden acumularse en espacios confinados.',
      health: 'TÓXICO y/o CORROSIVO; puede ser fatal si se inhala, ingiere o absorbe por la piel. El contacto puede causar quemaduras severas en piel y ojos. La inhalación puede causar daño pulmonar. La exposición repetida puede causar daño a órganos.',
    },
    publicSafety: {
      callNumber: '911 / ECU-911',
      isolationSmallSpill: 'Aislar al menos 50 m en todas las direcciones.',
      isolationLargeSpill: 'Aislar al menos 100 m en todas las direcciones.',
      fireIsolation: 'En incendio de tanques aislar 500 m.',
      protectiveActionSmall: 'Usar EPP; evacuar 100 m.',
      protectiveActionLarge: 'Evacuar 300 m a favor del viento.',
    },
    emergencyResponse: {
      fire: [
        'Usar SCBA presión positiva y ropa de protección química.',
        'Pequeño incendio: CO2, polvo seco o agua en neblina.',
        'Gran incendio: Agua en diluvio, niebla o espuma.',
        'Enfriar los contenedores con agua.',
        'Retirar los contenedores del área si puede hacerse sin riesgo.',
      ],
      spillLeak: [
        'No ingresar sin SCBA y EPP completo.',
        'No tocar ni caminar sobre material derramado.',
        'Detener la fuga si puede hacerse sin riesgo.',
        'Prevenir la entrada en sumideros, drenajes y cuerpos de agua.',
        'Absorber con material inerte seco y depositar en contenedores resistentes.',
        'No lavar al drenaje; recuperar para disposición adecuada.',
      ],
    },
    firstAid: [
      'Trasladar a la víctima a un área de aire fresco con EPP.',
      'Llamar al 911 urgente.',
      'Si no respira: respiración artificial con protección. NO boca a boca.',
      'Si la respiración es difícil: administrar oxígeno.',
      'Quitar INMEDIATAMENTE la ropa contaminada.',
      'Lavado cutáneo con agua corriente durante mínimo 20 minutos.',
      'Lavado ocular con agua durante mínimo 20 minutos.',
      'NO inducir el vómito.',
      'Trasladar URGENTEMENTE al hospital. Llevar ficha de seguridad.',
    ],
    fireByType: {
      small: 'CO2, polvo seco o agua en neblina.',
      large: 'Agua en diluvio, niebla o espuma.',
      tank: 'AISLAR 500 m. Enfriar contenedores con agua.',
    },
    ppe: {
      suit: 'Traje de protección química resistente a tóxicos y corrosivos (nivel B mínimo)',
      respirator: 'SCBA (equipo autónomo de respiración) presión positiva',
      gloves: 'Guantes de protección química resistentes a tóxicos y corrosivos',
      boots: 'Botas de seguridad resistentes a químicos corrosivos',
    },
  },

  154: {
    number: 154,
    name: 'Sustancias Tóxicas y/o Corrosivas (No Combustibles)',
    hazards: {
      fireExplosion: 'No son combustibles. El fuego puede producir gases irritantes, corrosivos o tóxicos. Los contenedores pueden explotar cuando se calientan.',
      health: 'TÓXICO y/o CORROSIVO; puede ser fatal si se inhala, ingiere o absorbe por la piel. El contacto puede causar quemaduras en piel y ojos. Las soluciones alcalinas fuertes (hidróxido de sodio) producen saponificación del tejido. La exposición puede no producir efectos inmediatos.',
    },
    publicSafety: {
      callNumber: '911 / ECU-911',
      isolationSmallSpill: 'Aislar al menos 25 m en todas las direcciones.',
      isolationLargeSpill: 'Aislar al menos 100 m en todas las direcciones.',
      fireIsolation: 'En incendio de tanques aislar 300 m.',
      protectiveActionSmall: 'Usar EPP adecuado.',
      protectiveActionLarge: 'Evacuar 300 m a favor del viento.',
    },
    emergencyResponse: {
      fire: [
        'Usar SCBA presión positiva y ropa protectora.',
        'Pequeño incendio: CO2, polvo seco o agua en neblina.',
        'Gran incendio: Agua en diluvio o niebla.',
        'Enfriar los contenedores con agua.',
        'Retirar los contenedores del área si puede hacerse sin riesgo.',
      ],
      spillLeak: [
        'No ingresar sin EPP adecuado (guantes, protección ocular, respirador).',
        'No tocar ni caminar sobre material derramado.',
        'Detener la fuga si puede hacerse sin riesgo.',
        'Para ácidos: neutralizar con bicarbonato de sodio. Para bases: neutralizar con ácido cítrico diluido.',
        'Recoger con material absorbente inerte y depositar en contenedores resistentes a corrosivos.',
        'Prevenir la entrada en drenajes y cuerpos de agua.',
      ],
    },
    firstAid: [
      'Trasladar a la víctima a un área de aire fresco.',
      'Llamar al 911.',
      'Si no respira: respiración artificial.',
      'Quitar INMEDIATAMENTE la ropa contaminada.',
      'Lavado cutáneo con abundante agua corriente durante mínimo 20 minutos.',
      'Lavado ocular con agua limpia durante mínimo 20 minutos. NO frotar.',
      'NO inducir el vómito (corrosivo).',
      'Trasladar al hospital. Línea gratuita CIATOX (MSP): 1800 836366.',
    ],
    fireByType: {
      small: 'CO2, polvo seco o agua en neblina.',
      large: 'Agua en diluvio o niebla.',
      tank: 'AISLAR 300 m. Enfriar contenedores con agua.',
    },
    ppe: {
      suit: 'Traje de protección química resistente a corrosivos',
      respirator: 'SCBA (equipo autónomo de respiración) presión positiva',
      gloves: 'Guantes de protección química resistentes a corrosivos',
      boots: 'Botas de seguridad resistentes a corrosivos',
    },
  },

  155: {
    number: 155,
    name: 'Sustancias Tóxicas y/o Corrosivas (Inflamables)',
    hazards: {
      fireExplosion: 'INFLAMABLES. El incendio puede producir gases muy tóxicos y/o corrosivos. Los vapores pueden acumularse. Los contenedores pueden explotar cuando se calientan.',
      health: 'TÓXICO y/o CORROSIVO; puede ser fatal si se inhala, ingiere o absorbe por la piel. El contacto con la piel puede causar quemaduras severas. Los vapores son irritantes para el tracto respiratorio y los ojos.',
    },
    publicSafety: {
      callNumber: '911 / ECU-911',
      isolationSmallSpill: 'Aislar al menos 50 m en todas las direcciones.',
      isolationLargeSpill: 'Aislar al menos 200 m en todas las direcciones.',
      fireIsolation: 'En incendio de tanques aislar 500 m.',
      protectiveActionSmall: 'Usar EPP; evacuar 100 m.',
      protectiveActionLarge: 'Evacuar 400 m a favor del viento.',
    },
    emergencyResponse: {
      fire: [
        'Usar SCBA presión positiva y traje de protección química.',
        'ELIMINAR todas las fuentes de ignición.',
        'Pequeño incendio: CO2, polvo seco o espuma.',
        'Gran incendio: Espuma o agua en diluvio.',
        'Enfriar los contenedores con agua.',
      ],
      spillLeak: [
        'ELIMINAR todas las fuentes de ignición.',
        'No ingresar sin SCBA y EPP completo.',
        'No tocar ni caminar sobre material derramado.',
        'Detener la fuga si puede hacerse sin riesgo.',
        'Absorber con material inerte y depositar en contenedores.',
        'Usar agua en neblina para reducir vapores.',
      ],
    },
    firstAid: [
      'Trasladar a la víctima a un área de aire fresco.',
      'Llamar al 911 URGENTE.',
      'Si no respira: respiración artificial con protección.',
      'Quitar INMEDIATAMENTE toda la ropa contaminada.',
      'Lavado cutáneo con agua corriente durante mínimo 20 minutos.',
      'Lavado ocular con agua durante mínimo 20 minutos.',
      'NO inducir el vómito.',
      'Trasladar urgentemente al hospital.',
    ],
    fireByType: {
      small: 'CO2, polvo seco o espuma.',
      large: 'Espuma o agua en diluvio.',
      tank: 'AISLAR 500 m. Enfriar contenedores con agua.',
    },
    ppe: {
      suit: 'Traje de protección química resistente a tóxicos (nivel B mínimo)',
      respirator: 'SCBA (equipo autónomo de respiración) presión positiva',
      gloves: 'Guantes de protección química resistentes a tóxicos e inflamables',
      boots: 'Botas de seguridad antiestáticas resistentes a tóxicos',
    },
  },

  156: {
    number: 156,
    name: 'Sustancias Tóxicas y/o Corrosivas (Reactivas)',
    hazards: {
      fireExplosion: 'REACTIVAS. Pueden reaccionar violentamente con agua, ácidos, bases u otros agentes, generando gases tóxicos o inflamables. El incendio puede producir gases muy tóxicos, corrosivos e irritantes.',
      health: 'TÓXICO y/o CORROSIVO; puede ser fatal. El contacto con la piel puede causar quemaduras severas. Los vapores son altamente tóxicos. La exposición puede no producir síntomas inmediatos pero puede causar daño retardado a órganos.',
    },
    publicSafety: {
      callNumber: '911 / ECU-911',
      isolationSmallSpill: 'Aislar al menos 100 m en todas las direcciones.',
      isolationLargeSpill: 'Aislar al menos 300 m en todas las direcciones.',
      fireIsolation: 'En incendio de tanques aislar 800 m.',
      protectiveActionSmall: 'Usar SCBA; evacuar 200 m.',
      protectiveActionLarge: 'Evacuar 500 m a favor del viento.',
    },
    emergencyResponse: {
      fire: [
        'Usar SCBA presión positiva y traje de protección química nivel A o B.',
        'NO usar agua directamente sobre la sustancia (puede reaccionar violentamente).',
        'Pequeño incendio: CO2 o polvo seco (NO agua si hay reactividad con agua).',
        'Gran incendio: Agua en neblina desde distancia segura.',
        'Enfriar los contenedores con agua pulverizada.',
      ],
      spillLeak: [
        'No ingresar sin SCBA y traje de protección química completo.',
        'EVITAR TODO CONTACTO con la sustancia.',
        'Detener la fuga si puede hacerse sin riesgo.',
        'Cubrir el derrame con tierra seca o vermiculita. NO con materiales reactivos.',
        'Prevenir la entrada en drenajes y cuerpos de agua.',
        'NO lavar con agua a menos que sea seguro según la SDS.',
      ],
    },
    firstAid: [
      'Trasladar a la víctima a un área de aire fresco con EPP completo.',
      'Llamar al 911 URGENTE.',
      'Si no respira: respiración artificial con máscara. NO boca a boca.',
      'Quitar INMEDIATAMENTE toda la ropa contaminada.',
      'Lavado cutáneo con abundante agua corriente durante mínimo 20 minutos.',
      'Lavado ocular con agua durante mínimo 20 minutos.',
      'NO inducir el vómito.',
      'Trasladar URGENTEMENTE al hospital. Llevar ficha de seguridad.',
    ],
    fireByType: {
      small: 'CO2 o polvo seco. NO agua si hay reactividad con agua.',
      large: 'Agua en neblina desde distancia segura.',
      tank: 'AISLAR 800 m. Enfriar desde distancia segura. Traje nivel A obligatorio.',
    },
    ppe: {
      suit: 'Traje de protección química nivel A (encapsulado) resistente a reactivos',
      respirator: 'SCBA (equipo autónomo de respiración) presión positiva',
      gloves: 'Guantes de protección química nivel A resistentes a reactivos tóxicos',
      boots: 'Botas de seguridad resistentes a químicos reactivos y corrosivos',
    },
  },

  157: {
    number: 157,
    name: 'Sustancias Tóxicas y/o Corrosivas (Reactivas con Agua)',
    hazards: {
      fireExplosion: 'REACTIVAS CON AGUA; reaccionan con el agua y la humedad produciendo gases tóxicos y/o corrosivos (p.ej. HCl, HF, SO2). El calor puede causar incendio. Los contenedores pueden explotar cuando se calientan.',
      health: 'TÓXICO y CORROSIVO; causa quemaduras severas en piel y ojos. Los gases producidos (ácido clorhídrico, fluorhídrico) son altamente tóxicos. La inhalación puede ser fatal. La exposición puede causar edema pulmonar retardado.',
    },
    publicSafety: {
      callNumber: '911 / ECU-911',
      isolationSmallSpill: 'Aislar al menos 50 m en todas las direcciones.',
      isolationLargeSpill: 'Aislar al menos 300 m en todas las direcciones.',
      fireIsolation: 'En incendio de tanques aislar 800 m.',
      protectiveActionSmall: 'Usar SCBA y EPP; evacuar 100 m.',
      protectiveActionLarge: 'Evacuar 500 m a favor del viento.',
    },
    emergencyResponse: {
      fire: [
        'Usar SCBA presión positiva y traje de protección química resistente a corrosivos.',
        'NO usar agua directamente sobre la sustancia (reacción violenta).',
        'Pequeño incendio: CO2 o polvo seco SOLAMENTE.',
        'Gran incendio: Agua en neblina SOLO si no hay reacción directa con la sustancia.',
        'Enfriar los contenedores con agua pulverizada desde distancia segura.',
        'Retirar los contenedores si puede hacerse sin riesgo.',
      ],
      spillLeak: [
        'No ingresar sin SCBA y EPP completo resistente a ácidos.',
        'MANTENER SECO. NO usar agua ni materiales húmedos.',
        'No tocar ni caminar sobre material derramado.',
        'Detener la fuga si puede hacerse sin riesgo.',
        'Cubrir con tierra SECA, vermiculita seca o arena seca. NO con cal o carbonatos húmedos.',
        'Transferir cuidadosamente a contenedores secos para disposición.',
        'Prevenir la entrada en drenajes y cuerpos de agua.',
        'Ventilar el área afectada.',
      ],
    },
    firstAid: [
      'Trasladar a la víctima a un área de aire fresco. Usar EPP para el rescate.',
      'Llamar al 911 URGENTE. Indicar el nombre del producto.',
      'Si no respira: respiración artificial con máscara de bolsa-válvula. NO boca a boca.',
      'Administrar oxígeno.',
      'Quitar INMEDIATAMENTE TODA la ropa y joyas contaminadas (quemadura química continúa).',
      'Lavado cutáneo con abundante agua corriente durante MÍNIMO 20 minutos.',
      'Lavado ocular con agua limpia durante MÍNIMO 20 minutos. NO frotar.',
      'Para ácido fluorhídrico: aplicar gel de gluconato de calcio si está disponible.',
      'NO inducir el vómito. Dar agua si está consciente y puede tragar.',
      'Trasladar URGENTEMENTE al hospital. Llevar la etiqueta o ficha de seguridad.',
    ],
    fireByType: {
      small: 'CO2 o polvo seco SOLAMENTE. NO usar agua.',
      large: 'Agua en neblina SOLO si no hay reacción directa con la sustancia.',
      tank: 'AISLAR 800 m. Enfriar con agua pulverizada desde distancia segura.',
    },
    ppe: {
      suit: 'Traje de protección química resistente a ácidos y corrosivos (nivel A o B)',
      respirator: 'SCBA (equipo autónomo de respiración) presión positiva',
      gloves: 'Guantes de protección química resistentes a ácidos y corrosivos',
      boots: 'Botas de seguridad resistentes a ácidos y corrosivos',
    },
  },

  160: {
    number: 160,
    name: 'Solventes Halogenados',
    hazards: {
      fireExplosion: 'Algunos pueden arder pero no se encienden fácilmente. El fuego puede producir gases ácidos (ácido clorhídrico), corrosivos e irritantes. Los vapores son más pesados que el aire. Los contenedores pueden explotar cuando se calientan.',
      health: 'NOCIVO si se inhala o entra en contacto con la piel. Los vapores pueden causar mareo, náuseas, cefalea o inconsciencia. Algunos son sospechosos de causar cáncer (tetracloroetileno, tricloroetileno). Pueden causar daño hepático y renal.',
    },
    publicSafety: {
      callNumber: '911 / ECU-911',
      isolationSmallSpill: 'Aislar al menos 25 m en todas las direcciones.',
      isolationLargeSpill: 'Aislar al menos 100 m en todas las direcciones.',
      fireIsolation: 'En incendio de tanques aislar 300 m.',
      protectiveActionSmall: 'Ventilar el área; usar EPP.',
      protectiveActionLarge: 'Evacuar 200 m a favor del viento.',
    },
    emergencyResponse: {
      fire: [
        'Usar SCBA y ropa de protección química.',
        'Pequeño incendio: CO2, polvo seco o espuma.',
        'Gran incendio: Agua en diluvio, niebla o espuma.',
        'NO usar chorro de agua directo.',
        'Enfriar los contenedores con agua.',
        'Retirar los contenedores del área si puede hacerse sin riesgo.',
      ],
      spillLeak: [
        'Ventilar el área afectada.',
        'No tocar ni caminar sobre material derramado.',
        'Detener la fuga si puede hacerse sin riesgo.',
        'Absorber con material inerte (tierra, arena) y depositar en contenedores.',
        'Prevenir la entrada en sumideros, drenajes y cuerpos de agua.',
        'No contaminar el suministro de agua.',
        'Para derrames grandes: usar espuma para suprimir vapores.',
      ],
    },
    firstAid: [
      'Trasladar a la víctima a un área de aire fresco.',
      'Llamar al 911 si hay sintomatología.',
      'Si no respira: respiración artificial.',
      'Si la respiración es difícil: administrar oxígeno.',
      'Quitar la ropa contaminada.',
      'Contacto con piel: lavar con agua y jabón durante 20 minutos.',
      'Contacto con ojos: irrigar con agua durante al menos 20 minutos.',
      'NO inducir el vómito.',
      'Trasladar al centro médico. Línea gratuita CIATOX (MSP): 1800 836366.',
    ],
    fireByType: {
      small: 'CO2, polvo seco o espuma.',
      large: 'Agua en diluvio, niebla o espuma. NO chorro directo.',
      tank: 'AISLAR 300 m. Enfriar contenedores con agua.',
    },
    ppe: {
      suit: 'Ropa de protección química resistente a solventes halogenados',
      respirator: 'SCBA (equipo autónomo de respiración) presión positiva',
      gloves: 'Guantes de protección química resistentes a solventes clorados',
      boots: 'Botas de seguridad resistentes a solventes halogenados',
    },
  },
};

// ── Mapeo de Números UN/NA → Número de Guía ERG 2024 ─────────────────────────

export const UN_TO_ERG: Record<string, number> = {
  '1001': 116,
  '1002': 122,
  '1003': 122,
  '1005': 125,
  '1006': 120,
  '1008': 125,
  '1009': 126,
  '1010': 116,
  '1011': 115,
  '1012': 115,
  '1013': 120,
  '1016': 119,
  '1017': 124,
  '1018': 126,
  '1020': 126,
  '1021': 126,
  '1022': 126,
  '1023': 119,
  '1026': 119,
  '1027': 115,
  '1028': 126,
  '1029': 126,
  '1030': 115,
  '1032': 118,
  '1033': 115,
  '1035': 115,
  '1036': 118,
  '1037': 115,
  '1038': 115,
  '1039': 115,
  '1040': 119,
  '1041': 115,
  '1043': 125,
  '1044': 126,
  '1045': 124,
  '1046': 120,
  '1048': 125,
  '1049': 115,
  '1050': 125,
  '1051': 117,
  '1052': 125,
  '1053': 117,
  '1055': 115,
  '1056': 120,
  '1057': 115,
  '1058': 120,
  '1060': 116,
  '1061': 118,
  '1062': 123,
  '1063': 115,
  '1064': 117,
  '1065': 120,
  '1066': 120,
  '1067': 124,
  '1069': 125,
  '1070': 122,
  '1071': 119,
  '1072': 122,
  '1073': 122,
  '1075': 115,
  '1076': 125,
  '1077': 115,
  '1078': 126,
  '1079': 125,
  '1080': 126,
  '1081': 116,
  '1082': 119,
  '1083': 118,
  '1085': 116,
  '1086': 116,
  '1087': 116,
  '1088': 127,
  '1089': 129,
  '1090': 127,
  '1091': 127,
  '1092': 131,
  '1093': 131,
  '1098': 131,
  '1099': 131,
  '1100': 131,
  '1104': 129,
  '1105': 129,
  '1106': 132,
  '1107': 129,
  '1108': 128,
  '1109': 129,
  '1110': 127,
  '1111': 130,
  '1112': 128,
  '1113': 129,
  '1114': 130,
  '1120': 129,
  '1123': 129,
  '1125': 132,
  '1126': 130,
  '1127': 130,
  '1128': 129,
  '1129': 129,
  '1130': 128,
  '1131': 131,
  '1133': 128,
  '1134': 130,
  '1135': 131,
  '1136': 128,
  '1139': 127,
  '1143': 131,
  '1144': 128,
  '1145': 128,
  '1146': 128,
  '1147': 130,
  '1148': 129,
  '1149': 128,
  '1150': 130,
  '1152': 130,
  '1153': 127,
  '1154': 132,
  '1155': 127,
  '1156': 127,
  '1157': 128,
  '1158': 132,
  '1159': 127,
  '1160': 132,
  '1161': 129,
  '1162': 155,
  '1163': 131,
  '1164': 130,
  '1165': 127,
  '1166': 127,
  '1167': 128,
  '1169': 127,
  '1170': 127,
  '1171': 127,
  '1172': 129,
  '1173': 129,
  '1175': 130,
  '1176': 129,
  '1177': 130,
  '1178': 130,
  '1179': 127,
  '1180': 130,
  '1181': 155,
  '1182': 155,
  '1183': 139,
  '1184': 131,
  '1185': 131,
  '1188': 127,
  '1189': 129,
  '1190': 129,
  '1191': 129,
  '1192': 129,
  '1193': 127,
  '1194': 131,
  '1195': 129,
  '1196': 155,
  '1197': 127,
  '1198': 132,
  '1199': 153,
  '1201': 127,
  '1202': 128,
  '1203': 128,
  '1204': 127,
  '1206': 128,
  '1207': 130,
  '1208': 128,
  '1210': 129,
  '1212': 129,
  '1213': 129,
  '1214': 132,
  '1216': 128,
  '1218': 130,
  '1219': 129,
  '1220': 129,
  '1221': 132,
  '1222': 130,
  '1223': 128,
  '1224': 127,
  '1228': 131,
  '1229': 129,
  '1230': 131,
  '1231': 129,
  '1233': 130,
  '1234': 127,
  '1235': 132,
  '1237': 129,
  '1238': 155,
  '1239': 131,
  '1242': 139,
  '1243': 129,
  '1244': 131,
  '1245': 127,
  '1246': 127,
  '1247': 129,
  '1248': 129,
  '1249': 127,
  '1250': 155,
  '1251': 131,
  '1259': 131,
  '1261': 129,
  '1262': 128,
  '1263': 128,
  '1264': 129,
  '1265': 128,
  '1266': 127,
  '1267': 128,
  '1268': 128,
  '1270': 128,
  '1272': 129,
  '1274': 129,
  '1275': 129,
  '1276': 129,
  '1277': 132,
  '1278': 129,
  '1279': 130,
  '1280': 127,
  '1281': 129,
  '1282': 129,
  '1286': 127,
  '1287': 127,
  '1288': 128,
  '1289': 132,
  '1292': 129,
  '1293': 127,
  '1294': 130,
  '1295': 139,
  '1296': 132,
  '1297': 132,
  '1298': 155,
  '1299': 128,
  '1300': 128,
  '1301': 129,
  '1302': 127,
  '1303': 130,
  '1304': 127,
  '1305': 155,
  '1306': 129,
  '1307': 130,
  '1308': 170,
  '1309': 170,
  '1310': 113,
  '1312': 133,
  '1313': 133,
  '1314': 133,
  '1318': 133,
  '1320': 113,
  '1321': 113,
  '1322': 113,
  '1323': 170,
  '1324': 133,
  '1325': 133,
  '1326': 170,
  '1327': 133,
  '1328': 133,
  '1330': 133,
  '1331': 133,
  '1332': 133,
  '1333': 170,
  '1334': 133,
  '1336': 113,
  '1337': 113,
  '1338': 133,
  '1339': 139,
  '1340': 139,
  '1341': 139,
  '1343': 139,
  '1344': 113,
  '1345': 133,
  '1346': 170,
  '1347': 113,
  '1348': 113,
  '1349': 113,
  '1350': 133,
  '1352': 170,
  '1353': 133,
  '1354': 113,
  '1355': 113,
  '1356': 113,
  '1357': 113,
  '1358': 170,
  '1360': 139,
  '1361': 133,
  '1362': 133,
  '1363': 135,
  '1364': 133,
  '1365': 133,
  '1369': 135,
  '1372': 133,
  '1373': 133,
  '1374': 133,
  '1376': 135,
  '1378': 170,
  '1379': 133,
  '1380': 135,
  '1381': 136,
  '1382': 135,
  '1383': 135,
  '1384': 135,
  '1385': 135,
  '1386': 135,
  '1387': 133,
  '1389': 138,
  '1390': 139,
  '1391': 138,
  '1392': 138,
  '1393': 138,
  '1394': 138,
  '1395': 139,
  '1396': 138,
  '1397': 139,
  '1398': 138,
  '1400': 138,
  '1401': 138,
  '1402': 138,
  '1403': 138,
  '1404': 138,
  '1405': 138,
  '1407': 138,
  '1408': 139,
  '1409': 138,
  '1410': 138,
  '1411': 138,
  '1413': 138,
  '1414': 138,
  '1415': 138,
  '1417': 138,
  '1418': 138,
  '1419': 139,
  '1420': 138,
  '1421': 138,
  '1422': 138,
  '1423': 138,
  '1426': 138,
  '1427': 138,
  '1428': 138,
  '1431': 138,
  '1432': 139,
  '1433': 139,
  '1435': 138,
  '1436': 138,
  '1437': 138,
  '1438': 140,
  '1439': 141,
  '1442': 143,
  '1444': 140,
  '1445': 141,
  '1446': 141,
  '1447': 141,
  '1448': 141,
  '1449': 141,
  '1450': 140,
  '1451': 140,
  '1452': 140,
  '1453': 140,
  '1454': 140,
  '1455': 140,
  '1456': 140,
  '1457': 140,
  '1458': 140,
  '1459': 140,
  '1461': 140,
  '1462': 143,
  '1463': 141,
  '1465': 140,
  '1466': 140,
  '1467': 143,
  '1469': 141,
  '1470': 141,
  '1471': 140,
  '1472': 143,
  '1473': 140,
  '1474': 140,
  '1475': 140,
  '1476': 140,
  '1477': 140,
  '1479': 140,
  '1481': 140,
  '1482': 140,
  '1483': 140,
  '1484': 140,
  '1485': 140,
  '1486': 140,
  '1487': 140,
  '1488': 140,
  '1489': 140,
  '1490': 140,
  '1491': 144,
  '1492': 140,
  '1493': 140,
  '1494': 140,
  '1495': 140,
  '1496': 143,
  '1498': 140,
  '1499': 140,
  '1500': 141,
  '1502': 140,
  '1503': 140,
  '1504': 144,
  '1505': 140,
  '1506': 143,
  '1507': 140,
  '1508': 140,
  '1509': 143,
  '1510': 143,
  '1511': 140,
  '1512': 140,
  '1513': 140,
  '1514': 140,
  '1515': 140,
  '1516': 143,
  '1517': 113,
  '1541': 156,
  '1544': 151,
  '1545': 131,
  '1546': 151,
  '1547': 153,
  '1548': 153,
  '1549': 157,
  '1550': 151,
  '1551': 151,
  '1553': 154,
  '1554': 154,
  '1555': 151,
  '1556': 152,
  '1557': 152,
  '1558': 152,
  '1559': 151,
  '1560': 157,
  '1561': 151,
  '1562': 152,
  '1564': 154,
  '1565': 157,
  '1566': 154,
  '1567': 134,
  '1569': 131,
  '1570': 151,
  '1571': 113,
  '1572': 151,
  '1573': 151,
  '1574': 151,
  '1575': 157,
  '1577': 153,
  '1578': 152,
  '1580': 154,
  '1581': 123,
  '1582': 119,
  '1583': 154,
  '1585': 151,
  '1586': 151,
  '1587': 151,
  '1588': 157,
  '1589': 125,
  '1590': 153,
  '1591': 152,
  '1593': 160,
  '1594': 152,
  '1595': 156,
  '1596': 153,
  '1597': 152,
  '1598': 153,
  '1599': 153,
  '1600': 152,
  '1601': 151,
  '1602': 151,
  '1603': 155,
  '1604': 132,
  '1605': 154,
  '1606': 151,
  '1607': 151,
  '1608': 151,
  '1611': 151,
  '1612': 123,
  '1613': 154,
  '1614': 152,
  '1616': 151,
  '1617': 151,
  '1618': 151,
  '1620': 151,
  '1621': 151,
  '1622': 151,
  '1623': 151,
  '1624': 154,
  '1625': 141,
  '1626': 157,
  '1627': 141,
  '1629': 151,
  '1630': 151,
  '1631': 154,
  '1634': 154,
  '1636': 154,
  '1637': 151,
  '1638': 151,
  '1639': 151,
  '1640': 151,
  '1641': 151,
  '1642': 151,
  '1643': 151,
  '1644': 151,
  '1645': 151,
  '1646': 151,
  '1647': 151,
  '1648': 127,
  '1649': 152,
  '1650': 153,
  '1651': 153,
  '1652': 153,
  '1653': 151,
  '1654': 151,
  '1655': 151,
  '1656': 151,
  '1657': 151,
  '1658': 151,
  '1659': 151,
  '1660': 124,
  '1661': 153,
  '1662': 152,
  '1663': 153,
  '1664': 152,
  '1665': 152,
  '1669': 151,
  '1670': 157,
  '1671': 153,
  '1672': 151,
  '1673': 153,
  '1674': 151,
  '1677': 151,
  '1678': 154,
  '1679': 157,
  '1680': 157,
  '1683': 151,
  '1684': 151,
  '1685': 151,
  '1686': 154,
  '1687': 153,
  '1688': 152,
  '1689': 157,
  '1690': 154,
  '1691': 151,
  '1692': 151,
  '1693': 159,
  '1694': 159,
  '1695': 131,
  '1697': 153,
  '1698': 154,
  '1699': 151,
  '1700': 159,
  '1701': 152,
  '1702': 151,
  '1704': 153,
  '1707': 151,
  '1708': 153,
  '1709': 151,
  '1710': 160,
  '1711': 153,
  '1712': 151,
  '1713': 151,
  '1714': 139,
  '1715': 137,
  '1716': 156,
  '1717': 155,
  '1718': 153,
  '1719': 154,
  '1722': 155,
  '1723': 132,
  '1724': 155,
  '1725': 137,
  '1726': 137,
  '1727': 154,
  '1728': 156,
  '1729': 156,
  '1730': 157,
  '1731': 157,
  '1732': 157,
  '1733': 157,
  '1736': 137,
  '1737': 156,
  '1738': 156,
  '1739': 137,
  '1740': 154,
  '1741': 125,
  '1742': 157,
  '1743': 157,
  '1744': 154,
  '1745': 144,
  '1746': 144,
  '1747': 155,
  '1748': 140,
  '1749': 124,
  '1750': 153,
  '1751': 153,
  '1752': 156,
  '1753': 156,
  '1754': 137,
  '1755': 154,
  '1756': 154,
  '1757': 154,
  '1758': 137,
  '1759': 154,
  '1760': 154,
  '1761': 154,
  '1762': 156,
  '1763': 156,
  '1764': 153,
  '1765': 156,
  '1766': 156,
  '1767': 155,
  '1768': 154,
  '1769': 156,
  '1770': 153,
  '1771': 156,
  '1773': 157,
  '1774': 154,
  '1775': 154,
  '1776': 154,
  '1777': 137,
  '1778': 154,
  '1779': 153,
  '1780': 156,
  '1781': 156,
  '1782': 154,
  '1783': 153,
  '1784': 156,
  '1786': 157,
  '1787': 154,
  '1788': 154,
  '1789': 157,
  '1790': 157,
  '1791': 154,
  '1792': 157,
  '1793': 153,
  '1794': 154,
  '1796': 157,
  '1798': 157,
  '1799': 156,
  '1800': 156,
  '1801': 156,
  '1802': 157,
  '1803': 153,
  '1804': 156,
  '1805': 154,
  '1806': 137,
  '1807': 137,
  '1808': 137,
  '1809': 137,
  '1810': 137,
  '1811': 154,
  '1812': 154,
  '1813': 154,
  '1814': 154,
  '1815': 155,
  '1816': 155,
  '1817': 137,
  '1818': 157,
  '1819': 154,
  '1823': 154,
  '1824': 154,
  '1825': 157,
  '1826': 157,
  '1827': 137,
  '1828': 137,
  '1829': 137,
  '1830': 137,
  '1831': 137,
  '1832': 137,
  '1833': 154,
  '1834': 137,
  '1835': 153,
  '1836': 137,
  '1837': 157,
  '1838': 137,
  '1839': 153,
  '1840': 154,
  '1841': 171,
  '1843': 141,
  '1845': 120,
  '1846': 151,
  '1847': 153,
  '1848': 153,
  '1849': 153,
  '1851': 151,
  '1854': 135,
  '1855': 135,
  '1856': 133,
  '1857': 133,
  '1858': 126,
  '1859': 125,
  '1860': 116,
  '1862': 130,
  '1863': 128,
  '1865': 128,
  '1866': 128,
  '1868': 134,
  '1869': 138,
  '1870': 138,
  '1871': 170,
  '1872': 140,
  '1873': 143,
  '1884': 157,
  '1885': 153,
  '1886': 156,
  '1887': 160,
  '1888': 151,
  '1889': 157,
  '1891': 131,
  '1892': 151,
  '1894': 151,
  '1895': 151,
  '1897': 160,
  '1898': 156,
  '1902': 153,
  '1903': 153,
  '1905': 154,
  '1906': 153,
  '1907': 154,
  '1908': 154,
  '1910': 157,
  '1911': 119,
  '1912': 115,
  '1913': 120,
  '1914': 130,
  '1915': 127,
  '1916': 152,
  '1917': 129,
  '1918': 130,
  '1919': 129,
  '1920': 128,
  '1921': 131,
  '1922': 132,
  '1923': 135,
  '1928': 138,
  '1929': 135,
  '1931': 171,
  '1932': 135,
  '1935': 157,
  '1938': 156,
  '1939': 137,
  '1940': 153,
  '1941': 171,
  '1942': 140,
  '1944': 133,
  '1945': 133,
  '1950': 126,
  '1951': 120,
  '1952': 126,
  '1953': 119,
  '1954': 115,
  '1955': 123,
  '1956': 126,
  '1957': 115,
  '1958': 126,
  '1959': 116,
  '1961': 115,
  '1962': 116,
  '1963': 120,
  '1964': 115,
  '1965': 115,
  '1966': 115,
  '1967': 123,
  '1968': 126,
  '1969': 115,
  '1970': 120,
  '1971': 115,
  '1972': 115,
  '1973': 126,
  '1974': 126,
  '1975': 124,
  '1976': 126,
  '1977': 120,
  '1978': 115,
  '1982': 126,
  '1983': 126,
  '1984': 126,
  '1986': 131,
  '1987': 127,
  '1988': 131,
  '1989': 129,
  '1990': 171,
  '1991': 131,
  '1992': 131,
  '1993': 128,
  '1994': 136,
  '1999': 130,
  '2000': 133,
  '2001': 133,
  '2002': 135,
  '2004': 135,
  '2006': 135,
  '2008': 135,
  '2009': 135,
  '2010': 138,
  '2011': 139,
  '2012': 139,
  '2013': 139,
  '2014': 140,
  '2015': 143,
  '2016': 151,
  '2017': 159,
  '2018': 152,
  '2019': 152,
  '2020': 153,
  '2021': 153,
  '2022': 153,
  '2023': 131,
  '2024': 151,
  '2025': 151,
  '2026': 151,
  '2027': 151,
  '2028': 153,
  '2029': 132,
  '2030': 153,
  '2031': 157,
  '2032': 157,
  '2033': 154,
  '2034': 115,
  '2035': 115,
  '2036': 120,
  '2037': 115,
  '2038': 152,
  '2044': 115,
  '2045': 130,
  '2046': 130,
  '2047': 129,
  '2048': 130,
  '2049': 130,
  '2050': 128,
  '2051': 132,
  '2052': 128,
  '2053': 129,
  '2054': 132,
  '2055': 128,
  '2056': 127,
  '2057': 128,
  '2058': 129,
  '2059': 127,
  '2067': 140,
  '2071': 140,
  '2073': 125,
  '2074': 153,
  '2075': 153,
  '2076': 153,
  '2077': 153,
  '2078': 156,
  '2079': 154,
  '2186': 125,
  '2187': 120,
  '2188': 119,
  '2189': 119,
  '2190': 124,
  '2191': 123,
  '2192': 119,
  '2193': 126,
  '2194': 125,
  '2195': 125,
  '2196': 125,
  '2197': 125,
  '2198': 125,
  '2199': 119,
  '2200': 116,
  '2201': 122,
  '2202': 117,
  '2203': 116,
  '2204': 119,
  '2205': 153,
  '2206': 156,
  '2208': 140,
  '2209': 153,
  '2210': 135,
  '2211': 171,
  '2212': 171,
  '2213': 133,
  '2214': 156,
  '2215': 156,
  '2216': 171,
  '2217': 135,
  '2218': 132,
  '2219': 129,
  '2222': 128,
  '2224': 152,
  '2225': 156,
  '2226': 156,
  '2227': 130,
  '2232': 153,
  '2233': 152,
  '2234': 130,
  '2235': 153,
  '2236': 156,
  '2237': 153,
  '2238': 129,
  '2239': 153,
  '2240': 154,
  '2241': 128,
  '2242': 128,
  '2243': 130,
  '2244': 129,
  '2245': 128,
  '2246': 128,
  '2247': 128,
  '2248': 132,
  '2249': 131,
  '2250': 156,
  '2251': 128,
  '2252': 127,
  '2253': 153,
  '2254': 133,
  '2256': 130,
  '2257': 138,
  '2258': 132,
  '2259': 153,
  '2260': 132,
  '2261': 153,
  '2262': 156,
  '2263': 128,
  '2264': 132,
  '2265': 129,
  '2266': 132,
  '2267': 156,
  '2269': 153,
  '2270': 132,
  '2271': 128,
  '2272': 153,
  '2273': 153,
  '2274': 153,
  '2275': 129,
  '2276': 132,
  '2277': 130,
  '2278': 128,
  '2279': 151,
  '2280': 153,
  '2281': 156,
  '2282': 129,
  '2283': 130,
  '2284': 131,
  '2285': 155,
  '2286': 128,
  '2287': 128,
  '2288': 128,
  '2289': 153,
  '2290': 156,
  '2291': 151,
  '2293': 128,
  '2294': 153,
  '2295': 131,
  '2296': 128,
  '2297': 128,
  '2298': 128,
  '2299': 156,
  '2300': 153,
  '2301': 128,
  '2302': 127,
  '2303': 128,
  '2304': 133,
  '2305': 153,
  '2306': 152,
  '2307': 152,
  '2308': 157,
  '2309': 128,
  '2310': 131,
  '2311': 153,
  '2312': 153,
  '2313': 129,
  '2315': 171,
  '2316': 157,
  '2317': 157,
  '2318': 135,
  '2319': 128,
  '2320': 153,
  '2321': 153,
  '2322': 152,
  '2323': 130,
  '2324': 128,
  '2325': 129,
  '2326': 153,
  '2327': 153,
  '2328': 156,
  '2329': 130,
  '2330': 128,
  '2331': 154,
  '2332': 129,
  '2333': 131,
  '2334': 131,
  '2335': 131,
  '2336': 131,
  '2337': 131,
  '2338': 127,
  '2339': 130,
  '2340': 130,
  '2341': 130,
  '2342': 130,
  '2343': 130,
  '2344': 129,
  '2345': 130,
  '2346': 127,
  '2347': 130,
  '2348': 129,
  '2350': 127,
  '2351': 129,
  '2352': 127,
  '2353': 155,
  '2354': 131,
  '2356': 129,
  '2357': 132,
  '2358': 128,
  '2359': 132,
  '2360': 131,
  '2361': 132,
  '2362': 130,
  '2363': 129,
  '2364': 128,
  '2366': 128,
  '2367': 130,
  '2368': 128,
  '2370': 128,
  '2371': 128,
  '2372': 129,
  '2373': 127,
  '2374': 127,
  '2375': 129,
  '2376': 127,
  '2377': 127,
  '2378': 131,
  '2379': 132,
  '2380': 127,
  '2381': 131,
  '2382': 131,
  '2383': 132,
  '2384': 127,
  '2385': 129,
  '2386': 132,
  '2387': 130,
  '2388': 130,
  '2389': 128,
  '2390': 129,
  '2391': 129,
  '2392': 129,
  '2393': 129,
  '2394': 129,
  '2395': 155,
  '2396': 131,
  '2397': 127,
  '2398': 127,
  '2399': 132,
  '2400': 130,
  '2401': 132,
  '2402': 130,
  '2403': 129,
  '2404': 131,
  '2405': 129,
  '2406': 127,
  '2407': 155,
  '2409': 129,
  '2410': 129,
  '2411': 131,
  '2412': 130,
  '2413': 128,
  '2414': 130,
  '2416': 129,
  '2417': 125,
  '2418': 125,
  '2419': 116,
  '2420': 125,
  '2421': 124,
  '2422': 126,
  '2424': 126,
  '2426': 140,
  '2427': 140,
  '2428': 140,
  '2429': 140,
  '2430': 153,
  '2431': 153,
  '2432': 153,
  '2433': 152,
  '2434': 156,
  '2435': 156,
  '2436': 129,
  '2437': 156,
  '2438': 131,
  '2439': 154,
  '2440': 154,
  '2441': 135,
  '2442': 156,
  '2443': 137,
  '2444': 137,
  '2446': 153,
  '2447': 136,
  '2448': 133,
  '2451': 122,
  '2452': 116,
  '2453': 115,
  '2454': 115,
  '2455': 116,
  '2456': 130,
  '2457': 128,
  '2458': 130,
  '2459': 128,
  '2460': 128,
  '2461': 128,
  '2463': 138,
  '2464': 141,
  '2465': 140,
  '2466': 143,
  '2468': 140,
  '2469': 140,
  '2470': 152,
  '2471': 154,
  '2473': 154,
  '2474': 156,
  '2475': 157,
  '2477': 131,
  '2478': 155,
  '2480': 155,
  '2481': 155,
  '2482': 155,
  '2483': 155,
  '2484': 155,
  '2485': 155,
  '2486': 155,
  '2487': 155,
  '2488': 155,
  '2490': 153,
  '2491': 153,
  '2493': 132,
  '2495': 144,
  '2496': 156,
  '2498': 129,
  '2501': 152,
  '2502': 132,
  '2503': 137,
  '2504': 159,
  '2505': 154,
  '2506': 154,
  '2507': 154,
  '2508': 156,
  '2509': 154,
  '2511': 153,
  '2512': 152,
  '2513': 156,
  '2514': 130,
  '2515': 159,
  '2516': 151,
  '2517': 115,
  '2518': 153,
  '2520': 130,
  '2521': 131,
  '2522': 153,
  '2524': 129,
  '2525': 156,
  '2526': 132,
  '2527': 129,
  '2528': 130,
  '2529': 132,
  '2531': 153,
  '2533': 156,
  '2534': 119,
  '2535': 132,
  '2536': 127,
  '2538': 133,
  '2541': 128,
  '2542': 153,
  '2545': 135,
  '2546': 135,
  '2547': 143,
  '2548': 124,
  '2552': 151,
  '2554': 130,
  '2555': 113,
  '2556': 113,
  '2557': 133,
  '2558': 131,
  '2560': 129,
  '2561': 128,
  '2564': 153,
  '2565': 153,
  '2567': 154,
  '2570': 154,
  '2571': 156,
  '2572': 153,
  '2573': 141,
  '2574': 151,
  '2576': 137,
  '2577': 156,
  '2578': 157,
  '2579': 153,
  '2580': 154,
  '2581': 154,
  '2582': 154,
  '2583': 153,
  '2584': 153,
  '2585': 153,
  '2586': 153,
  '2587': 153,
  '2588': 151,
  '2589': 155,
  '2590': 171,
  '2591': 120,
  '2599': 126,
  '2601': 115,
  '2602': 126,
  '2603': 131,
  '2604': 132,
  '2605': 155,
  '2606': 155,
  '2607': 129,
  '2608': 129,
  '2609': 156,
  '2610': 132,
  '2611': 131,
  '2612': 127,
  '2614': 129,
  '2615': 127,
  '2616': 129,
  '2617': 129,
  '2618': 130,
  '2619': 132,
  '2620': 130,
  '2621': 127,
  '2622': 131,
  '2623': 133,
  '2624': 138,
  '2626': 140,
  '2627': 140,
  '2628': 151,
  '2629': 151,
  '2630': 151,
  '2642': 154,
  '2643': 153,
  '2644': 151,
  '2645': 153,
  '2646': 151,
  '2647': 153,
  '2648': 154,
  '2649': 153,
  '2650': 153,
  '2651': 153,
  '2653': 156,
  '2655': 151,
  '2656': 154,
  '2657': 153,
  '2659': 151,
  '2660': 153,
  '2661': 153,
  '2664': 160,
  '2667': 152,
  '2668': 131,
  '2669': 152,
  '2670': 157,
  '2671': 153,
  '2672': 154,
  '2673': 151,
  '2674': 154,
  '2676': 119,
  '2677': 154,
  '2678': 154,
  '2679': 154,
  '2680': 154,
  '2681': 154,
  '2682': 157,
  '2683': 132,
  '2684': 132,
  '2685': 132,
  '2686': 132,
  '2687': 133,
  '2688': 159,
  '2689': 153,
  '2690': 152,
  '2691': 137,
  '2692': 157,
  '2693': 154,
  '2698': 156,
  '2699': 154,
  '2705': 153,
  '2707': 127,
  '2709': 128,
  '2710': 128,
  '2713': 153,
  '2714': 133,
  '2715': 133,
  '2716': 153,
  '2717': 133,
  '2719': 141,
  '2720': 141,
  '2721': 140,
  '2722': 140,
  '2723': 140,
  '2724': 140,
  '2725': 140,
  '2726': 140,
  '2727': 141,
  '2728': 140,
  '2729': 152,
  '2730': 152,
  '2732': 152,
  '2733': 132,
  '2734': 132,
  '2735': 153,
  '2738': 153,
  '2739': 156,
  '2740': 155,
  '2741': 141,
  '2742': 155,
  '2743': 155,
  '2744': 155,
  '2745': 157,
  '2746': 156,
  '2747': 156,
  '2748': 156,
  '2749': 130,
  '2750': 153,
  '2751': 156,
  '2752': 127,
  '2753': 153,
  '2754': 153,
  '2757': 151,
  '2758': 131,
  '2759': 151,
  '2760': 131,
  '2761': 151,
  '2762': 131,
  '2763': 151,
  '2764': 131,
  '2771': 151,
  '2772': 131,
  '2775': 151,
  '2776': 131,
  '2777': 151,
  '2778': 131,
  '2779': 153,
  '2780': 131,
  '2781': 151,
  '2782': 131,
  '2783': 152,
  '2784': 131,
  '2785': 152,
  '2786': 153,
  '2787': 131,
  '2788': 153,
  '2789': 132,
  '2790': 153,
  '2793': 170,
  '2794': 154,
  '2795': 154,
  '2796': 157,
  '2797': 154,
  '2798': 137,
  '2799': 137,
  '2800': 154,
  '2801': 154,
  '2802': 154,
  '2803': 172,
  '2805': 138,
  '2806': 139,
  '2807': 171,
  '2809': 172,
  '2810': 153,
  '2811': 154,
  '2812': 154,
  '2813': 138,
  '2814': 158,
  '2815': 153,
  '2817': 154,
  '2818': 154,
  '2819': 153,
  '2820': 153,
  '2821': 153,
  '2822': 153,
  '2823': 153,
  '2826': 155,
  '2829': 153,
  '2830': 139,
  '2831': 160,
  '2834': 154,
  '2835': 138,
  '2837': 154,
  '2838': 129,
  '2839': 153,
  '2840': 129,
  '2841': 131,
  '2842': 129,
  '2844': 138,
  '2845': 135,
  '2846': 135,
  '2849': 153,
  '2850': 128,
  '2851': 157,
  '2852': 113,
  '2853': 151,
  '2854': 151,
  '2855': 151,
  '2856': 151,
  '2857': 126,
  '2858': 170,
  '2859': 154,
  '2861': 151,
  '2862': 151,
  '2863': 154,
  '2864': 151,
  '2865': 154,
  '2869': 157,
  '2870': 135,
  '2871': 170,
  '2872': 159,
  '2873': 153,
  '2874': 153,
  '2875': 151,
  '2876': 153,
  '2878': 170,
  '2879': 157,
  '2880': 140,
  '2881': 135,
  '2900': 158,
  '2901': 124,
  '2902': 151,
  '2903': 131,
  '2904': 154,
  '2905': 154,
  '2907': 133,
  '2908': 161,
  '2909': 161,
  '2910': 161,
  '2911': 161,
  '2912': 162,
  '2913': 162,
  '2915': 163,
  '2916': 163,
  '2917': 163,
  '2919': 163,
  '2920': 132,
  '2921': 134,
  '2922': 154,
  '2923': 154,
  '2924': 132,
  '2925': 134,
  '2926': 134,
  '2927': 154,
  '2928': 154,
  '2929': 131,
  '2930': 134,
  '2931': 151,
  '2933': 129,
  '2934': 129,
  '2935': 129,
  '2936': 153,
  '2937': 153,
  '2940': 135,
  '2941': 153,
  '2942': 153,
  '2943': 129,
  '2945': 132,
  '2946': 153,
  '2947': 127,
  '2948': 153,
  '2949': 154,
  '2950': 138,
  '2956': 149,
  '2965': 139,
  '2966': 153,
  '2967': 154,
  '2968': 135,
  '2969': 171,
  '2977': 166,
  '2978': 166,
  '2983': 131,
  '2984': 140,
  '2985': 155,
  '2986': 155,
  '2987': 156,
  '2988': 139,
  '2989': 133,
  '2990': 171,
  '2991': 131,
  '2992': 151,
  '2993': 131,
  '2994': 151,
  '2995': 131,
  '2996': 151,
  '2997': 131,
  '2998': 151,
  '3002': 151,
  '3005': 131,
  '3006': 151,
  '3009': 131,
  '3010': 151,
  '3011': 131,
  '3012': 151,
  '3013': 131,
  '3014': 153,
  '3015': 131,
  '3016': 151,
  '3017': 131,
  '3018': 152,
  '3019': 131,
  '3020': 153,
  '3021': 131,
  '3022': 127,
  '3023': 131,
  '3024': 131,
  '3025': 131,
  '3026': 151,
  '3027': 151,
  '3028': 154,
  '3048': 157,
  '3054': 129,
  '3055': 154,
  '3056': 129,
  '3057': 125,
  '3064': 127,
  '3065': 127,
  '3066': 153,
  '3070': 126,
  '3071': 131,
  '3072': 171,
  '3073': 131,
  '3077': 171,
  '3078': 138,
  '3079': 131,
  '3080': 155,
  '3082': 171,
  '3083': 124,
  '3084': 157,
  '3085': 140,
  '3086': 141,
  '3087': 141,
  '3088': 135,
  '3089': 170,
  '3090': 138,
  '3091': 138,
  '3092': 129,
  '3093': 157,
  '3094': 138,
  '3095': 136,
  '3096': 138,
  '3097': 140,
  '3098': 140,
  '3099': 142,
  '3100': 135,
  '3101': 146,
  '3102': 146,
  '3103': 146,
  '3104': 146,
  '3105': 145,
  '3106': 145,
  '3107': 145,
  '3108': 145,
  '3109': 145,
  '3110': 145,
  '3111': 148,
  '3112': 148,
  '3113': 148,
  '3114': 148,
  '3115': 148,
  '3116': 148,
  '3117': 148,
  '3118': 148,
  '3119': 148,
  '3120': 148,
  '3121': 144,
  '3122': 142,
  '3123': 139,
  '3124': 136,
  '3125': 139,
  '3126': 136,
  '3127': 135,
  '3128': 136,
  '3129': 138,
  '3130': 139,
  '3131': 138,
  '3132': 138,
  '3133': 138,
  '3134': 139,
  '3135': 138,
  '3136': 120,
  '3137': 140,
  '3138': 115,
  '3139': 140,
  '3140': 151,
  '3141': 157,
  '3142': 151,
  '3143': 151,
  '3144': 151,
  '3145': 153,
  '3146': 153,
  '3147': 154,
  '3148': 138,
  '3149': 140,
  '3150': 115,
  '3151': 171,
  '3152': 171,
  '3153': 115,
  '3154': 115,
  '3155': 154,
  '3156': 122,
  '3157': 122,
  '3158': 120,
  '3159': 126,
  '3160': 119,
  '3161': 115,
  '3162': 123,
  '3163': 126,
  '3164': 126,
  '3165': 131,
  '3166': 115,
  '3167': 115,
  '3168': 119,
  '3169': 123,
  '3170': 138,
  '3171': 147,
  '3172': 152,
  '3174': 135,
  '3175': 133,
  '3176': 133,
  '3178': 133,
  '3179': 134,
  '3180': 134,
  '3181': 133,
  '3182': 170,
  '3183': 135,
  '3184': 136,
  '3185': 136,
  '3186': 135,
  '3187': 136,
  '3188': 136,
  '3189': 135,
  '3190': 135,
  '3191': 136,
  '3192': 136,
  '3194': 135,
  '3200': 135,
  '3205': 135,
  '3206': 136,
  '3208': 138,
  '3209': 138,
  '3210': 140,
  '3211': 140,
  '3212': 140,
  '3213': 140,
  '3214': 140,
  '3215': 140,
  '3216': 140,
  '3218': 140,
  '3219': 140,
  '3220': 126,
  '3221': 149,
  '3222': 149,
  '3223': 149,
  '3224': 149,
  '3225': 149,
  '3226': 149,
  '3227': 149,
  '3228': 149,
  '3229': 149,
  '3230': 149,
  '3231': 150,
  '3232': 150,
  '3233': 150,
  '3234': 150,
  '3235': 150,
  '3236': 150,
  '3237': 150,
  '3238': 150,
  '3239': 150,
  '3240': 150,
  '3241': 133,
  '3242': 149,
  '3243': 151,
  '3244': 154,
  '3245': 171,
  '3246': 156,
  '3247': 140,
  '3248': 131,
  '3249': 151,
  '3250': 153,
  '3251': 133,
  '3252': 115,
  '3253': 154,
  '3254': 135,
  '3255': 135,
  '3256': 128,
  '3257': 171,
  '3258': 171,
  '3259': 154,
  '3260': 154,
  '3261': 154,
  '3262': 154,
  '3263': 154,
  '3264': 154,
  '3265': 153,
  '3266': 154,
  '3267': 153,
  '3268': 171,
  '3269': 128,
  '3270': 133,
  '3271': 127,
  '3272': 127,
  '3273': 131,
  '3274': 132,
  '3275': 131,
  '3276': 151,
  '3277': 154,
  '3278': 151,
  '3279': 131,
  '3280': 151,
  '3281': 151,
  '3282': 151,
  '3283': 151,
  '3284': 151,
  '3285': 151,
  '3286': 131,
  '3287': 151,
  '3288': 151,
  '3289': 154,
  '3290': 154,
  '3291': 158,
  '3292': 138,
  '3293': 153,
  '3294': 131,
  '3295': 128,
  '3296': 126,
  '3297': 126,
  '3298': 126,
  '3299': 126,
  '3300': 119,
  '3301': 136,
  '3302': 152,
  '3303': 124,
  '3304': 125,
  '3305': 119,
  '3306': 124,
  '3307': 124,
  '3308': 125,
  '3309': 119,
  '3310': 124,
  '3311': 122,
  '3312': 115,
  '3313': 135,
  '3314': 171,
  '3315': 151,
  '3316': 171,
  '3317': 113,
  '3318': 125,
  '3319': 113,
  '3320': 157,
  '3321': 162,
  '3322': 162,
  '3323': 163,
  '3324': 165,
  '3325': 165,
  '3326': 165,
  '3327': 165,
  '3328': 165,
  '3329': 165,
  '3330': 165,
  '3331': 165,
  '3332': 164,
  '3333': 165,
  '3334': 171,
  '3335': 171,
  '3336': 130,
  '3337': 126,
  '3338': 126,
  '3339': 126,
  '3340': 126,
  '3341': 135,
  '3342': 135,
  '3343': 113,
  '3344': 113,
  '3345': 153,
  '3346': 131,
  '3347': 131,
  '3348': 153,
  '3349': 151,
  '3350': 131,
  '3351': 131,
  '3352': 151,
  '3354': 115,
  '3355': 119,
  '3356': 140,
  '3357': 113,
  '3358': 115,
  '3359': 171,
  '3360': 133,
  '3361': 156,
  '3362': 155,
  '3363': 171,
  '3364': 113,
  '3365': 113,
  '3366': 113,
  '3367': 113,
  '3368': 113,
  '3369': 113,
  '3370': 113,
  '3371': 129,
  '3373': 158,
  '3374': 116,
  '3375': 140,
  '3376': 113,
  '3377': 140,
  '3378': 140,
  '3379': 113,
  '3380': 113,
  '3381': 151,
  '3382': 151,
  '3383': 131,
  '3384': 131,
  '3385': 139,
  '3386': 139,
  '3387': 142,
  '3388': 142,
  '3389': 154,
  '3390': 154,
  '3391': 135,
  '3392': 135,
  '3393': 135,
  '3394': 135,
  '3395': 135,
  '3396': 138,
  '3397': 138,
  '3398': 135,
  '3399': 138,
  '3400': 138,
  '3401': 138,
  '3402': 138,
  '3403': 138,
  '3404': 138,
  '3405': 141,
  '3406': 141,
  '3407': 140,
  '3408': 141,
  '3409': 152,
  '3410': 153,
  '3411': 153,
  '3412': 153,
  '3413': 157,
  '3414': 157,
  '3415': 154,
  '3416': 153,
  '3417': 152,
  '3418': 151,
  '3419': 157,
  '3420': 157,
  '3421': 154,
  '3422': 154,
  '3423': 153,
  '3424': 141,
  '3425': 156,
  '3426': 153,
  '3427': 153,
  '3428': 156,
  '3429': 153,
  '3430': 153,
  '3431': 152,
  '3432': 171,
  '3434': 153,
  '3436': 151,
  '3437': 152,
  '3438': 153,
  '3439': 151,
  '3440': 151,
  '3441': 153,
  '3442': 153,
  '3443': 152,
  '3444': 151,
  '3445': 151,
  '3446': 152,
  '3447': 152,
  '3448': 159,
  '3449': 159,
  '3450': 151,
  '3451': 153,
  '3452': 153,
  '3453': 154,
  '3454': 152,
  '3455': 153,
  '3456': 157,
  '3457': 152,
  '3458': 152,
  '3459': 152,
  '3460': 153,
  '3462': 152,
  '3463': 153,
  '3464': 151,
  '3465': 151,
  '3466': 151,
  '3467': 151,
  '3468': 115,
  '3469': 132,
  '3470': 132,
  '3471': 154,
  '3472': 153,
  '3473': 128,
  '3474': 113,
  '3475': 127,
  '3476': 138,
  '3477': 153,
  '3478': 115,
  '3479': 115,
  '3480': 147,
  '3481': 147,
  '3482': 138,
  '3483': 131,
  '3484': 132,
  '3485': 140,
  '3486': 140,
  '3487': 140,
  '3488': 131,
  '3489': 131,
  '3490': 155,
  '3491': 155,
  '3492': 131,
  '3493': 131,
  '3494': 131,
  '3495': 154,
  '3496': 171,
  '3497': 133,
  '3498': 157,
  '3499': 171,
  '3500': 126,
  '3501': 115,
  '3502': 123,
  '3503': 125,
  '3504': 119,
  '3505': 118,
  '3506': 172,
  '3507': 166,
  '3508': 171,
  '3509': 171,
  '3510': 174,
  '3511': 174,
  '3512': 173,
  '3513': 174,
  '3514': 173,
  '3515': 173,
  '3516': 173,
  '3517': 173,
  '3518': 173,
  '3519': 173,
  '3520': 173,
  '3521': 173,
  '3522': 173,
  '3523': 173,
  '3524': 173,
  '3525': 173,
  '3526': 173,
  '3527': 128,
  '3528': 128,
  '3529': 115,
  '3530': 171,
  '3531': 149,
  '3532': 149,
  '3533': 150,
  '3534': 150,
  '3535': 134,
  '3536': 147,
  '3537': 115,
  '3538': 120,
  '3539': 123,
  '3540': 127,
  '3541': 133,
  '3542': 135,
  '3543': 138,
  '3544': 140,
  '3545': 145,
  '3546': 151,
  '3547': 154,
  '3548': 171,
  '3549': 158,
  '3550': 151,
  '3551': 147,
  '3552': 147,
  '3553': 116,
  '3554': 172,
  '3555': 113,
  '3556': 147,
  '3557': 138,
  '3558': 147,
  '3559': 171,
  '3560': 153,
  '8000': 171,
  '9035': 123,
  '9191': 143,
  '9202': 168,
  '9206': 137,
  '9260': 169,
  '9263': 156,
  '9264': 151,
  '9269': 132,
};

// ── Funciones utilitarias ─────────────────────────────────────────────────────

export function getErgGuideNumber(unNumber: string | null | undefined): number | null {
  if (!unNumber) return null;
  const clean = unNumber.trim().replace(/^(UN|NA)\s*/i, '');
  return UN_TO_ERG[clean] ?? null;
}

export function getErgGuide(unNumber: string | null | undefined): ErgGuide | null {
  const guideNum = getErgGuideNumber(unNumber);
  if (guideNum === null) return null;
  return ERG_GUIDES[guideNum] ?? null;
}

export function getUniqueErgGuides(unNumbers: (string | null | undefined)[]): ErgGuide[] {
  const seen = new Set<number>();
  const guides: ErgGuide[] = [];
  for (const un of unNumbers) {
    const guideNum = getErgGuideNumber(un);
    if (guideNum !== null && !seen.has(guideNum)) {
      const guide = ERG_GUIDES[guideNum];
      if (guide) {
        seen.add(guideNum);
        guides.push(guide);
      }
    }
  }
  return guides;
}
